import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { 
  getRapportiniByCantiereId, 
  createRapportino, 
  updateRapportino, 
  deleteRapportino,
  duplicateRapportino,
  getAllUsers,
  getMezziAttrezzature,
  getMaterialiCostoVariabile,
  getMaterialiCostoFisso,
  getLavorazioni,
  getUserDisplayName,
  uploadRapportinoFile,
  createMaterialeCostoVariabile,  // 🆕 AGGIUNGI
  createMaterialeCostoFisso        // 🆕 AGGIUNGI
} from '../../../firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { getCostoPersona } from '../../../utils/costiUtils';
import './RapportiniManagement.css'; // CSS esterno
import './RapportiniManagement-mobile-improvements.css';
import {
  MezziAutocomplete,
  MaterialiVariabiliAutocomplete,
  MaterialiFissiAutocomplete,
  LavorazioniAutocomplete
} from './AutocompleteComponents';
import AutoPersonaleSection, { syncPendingManualHours } from './AutoPersonaleSection';
import SettingsDropdownPortal from './SettingsDropdownPortal';
// 🆕 NUOVI IMPORT PER LA SINCRONIZZAZIONE CLIENT-SIDE
import { useClientSideWorkHoursSync } from '../../../hooks/useClientSideWorkHoursSync';
import { syncRapportiniAfterWorkHoursChange } from '../../../services/clientSideRapportiniSync';
import { triggerBatchDistribution } from '../../../services/proportionalHoursDistribution';
import { useCollapsibleSections } from '../../../hooks/useCollapsibleSections';
import { CollapsibleFormSection, SectionControls } from '../../shared/CollapsibleFormSection';
import { useScrollPreservation } from '../../../hooks/useScrollPreservation';

import SyncStatusBadge from '../../shared/SyncStatusBadge';
import DatePickerModal from '../../shared/DatePickerModal';

const formatEuro = (valore) => {
  if (typeof valore !== 'number') return '';
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(valore);
};


// Componente di ricerca con suggerimenti
const SearchWithSuggestions = ({ 
  placeholder, 
  value, 
  onChange, 
  suggestions, 
  onSelectSuggestion, 
  getSuggestionText,
  renderSuggestion,
  maxSuggestions = 5 
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);

  const filteredSuggestions = suggestions
    .filter(item => {
      const text = getSuggestionText(item).toLowerCase();
      return text.includes(value.toLowerCase());
    })
    .slice(0, maxSuggestions);

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    onChange(inputValue);
    setShowSuggestions(inputValue.length > 0);
    setActiveSuggestion(-1);
  };

  const handleSuggestionClick = (suggestion) => {
    onSelectSuggestion(suggestion);
    setShowSuggestions(false);
    setActiveSuggestion(-1);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestion(prev => 
        prev < filteredSuggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestion(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeSuggestion >= 0 && filteredSuggestions[activeSuggestion]) {
        handleSuggestionClick(filteredSuggestions[activeSuggestion]);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setActiveSuggestion(-1);
    }
  };

  return (
    <div className="search-with-suggestions">
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => value.length > 0 && setShowSuggestions(true)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        className="search-input"
      />
      
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div className="suggestions-dropdown">
          {filteredSuggestions.map((suggestion, index) => (
            <div
              key={index}
              className={`suggestion-item ${index === activeSuggestion ? 'active' : ''}`}
              onClick={() => handleSuggestionClick(suggestion)}
              onMouseEnter={() => setActiveSuggestion(index)}
            >
              {renderSuggestion ? renderSuggestion(suggestion, value) : getSuggestionText(suggestion)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Componente principale con split view
const RapportiniManagement = ({ cantiere, onBack }) => {

  const handleNewRapportino = () => {
    const oggi = new Date().toISOString().split('T')[0];
  
    setEditingRapportino(null);
    setSelectedRapportino(null);
    setFormData({
      data: oggi,
      redattoDa: currentUser?.uid || '',
      personalePresente: [],
      mezziUtilizzati: [],
      materialiVariabili: [],
      materialiFissi: [],
      lavorazioni: [],
      ricavi: [],
      speseVarie: [],  // 🆕 NUOVO
      noteVarie: '',
      allegati: []
    });
    setShowForm(true);

    console.log("🆕 Nuovo rapportino inizializzato.");
  };
  const { currentUser } = useAuth();
  
  // Stati principali
  const [rapportini, setRapportini] = useState([]);
const [isLoading, setIsLoading] = useState(true);
const [showForm, setShowForm] = useState(false);
const [editingRapportino, setEditingRapportino] = useState(null);
const [selectedRapportino, setSelectedRapportino] = useState(null);
const [notification, setNotification] = useState({ show: false, message: '', type: '' });

// Stati per la creazione rapida materiali
const [showNewMaterialeVariabileModal, setShowNewMaterialeVariabileModal] = useState(false);
const [showNewMaterialeFissoModal, setShowNewMaterialeFissoModal] = useState(false);
const [newMaterialeVariabileData, setNewMaterialeVariabileData] = useState({
  nomeMacrovoce: ''
});
const [newMaterialeFissoData, setNewMaterialeFissoData] = useState({
  nomeMateriale: '',
  prezzoUnitario: '',
  unitaMisura: 'pz'
});
const [isCreatingMateriale, setIsCreatingMateriale] = useState(false);

const [showCopyMezziModal, setShowCopyMezziModal] = useState(false);
const [availableDatesForCopy, setAvailableDatesForCopy] = useState([]);
const [selectedDateForCopy, setSelectedDateForCopy] = useState('');

// Aggiungi questi stati insieme agli altri useState:
const [showDateModal, setShowDateModal] = useState(false);
const [rapportinoToDuplicate, setRapportinoToDuplicate] = useState(null);

const scrollContainerRef = useScrollPreservation();

// ✅ AGGIUNGI QUESTI NUOVI STATI:
const [searchTerm, setSearchTerm] = useState('');
const [filteredRapportini, setFilteredRapportini] = useState([]);
const [openSettingsDropdown, setOpenSettingsDropdown] = useState(null);
const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
const [isMobile, setIsMobile] = useState(false);
  // 🆕 NUOVI STATI PER LA SINCRONIZZAZIONE
  const [syncStatus, setSyncStatus] = useState(null);
  
  // Stati per il form rapido
  const [selectedSection, setSelectedSection] = useState('informazioni-base');
  const [formData, setFormData] = useState({
    data: new Date().toISOString().split('T')[0],
    redattoDa: currentUser?.uid || '',
    personalePresente: [],
    mezziUtilizzati: [],
    materialiVariabili: [],
    materialiFissi: [],
    lavorazioni: [],
    ricavi: [],
    speseVarie: [],  // 🆕 NUOVO CAMPO
    noteVarie: '',
    allegati: []
  });
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Dati di supporto
  const [users, setUsers] = useState([]);
  const [mezzi, setMezzi] = useState([]);
  const [materialiVariabili, setMaterialiVariabili] = useState([]);
  const [materialiFissi, setMaterialiFissi] = useState([]);
  const [lavorazioni, setLavorazioni] = useState([]);
  
  // Stato per nomi utenti
  const [userNames, setUserNames] = useState({});
  
  // Stati per le ricerche
  const [searchPersonale, setSearchPersonale] = useState('');
  const [searchMezzi, setSearchMezzi] = useState('');
  const [searchMatVariabili, setSearchMatVariabili] = useState('');
  const [searchMatFissi, setSearchMatFissi] = useState('');
  const [searchLavorazioni, setSearchLavorazioni] = useState('');

  // NUOVO: Sezioni del form per admin (CON ricavi)
  const formSections = [
    'informazioni-base',
    'personale-presente', 
    'mezzi-utilizzati',
    'materiali-variabili',
    'materiali-fissi',
    'lavorazioni',
    'ricavi', // Solo per admin
    'spese-varie', // 🆕 NUOVA SEZIONE
    'allegati',
    'note-aggiuntive'
  ];

  // Hook per sezioni collassabili
  const { 
    toggleSection: originalToggleSection, 
    isSectionCollapsed, 
    expandAllSections, 
    collapseAllSections,
    collapsedSections 
  } = useCollapsibleSections(formSections, {
    accordionMode: true, // ✅ AGGIUNGI QUESTA OPZIONE
    defaultOpen: 'informazioni-base' // Prima sezione aperta di default
  });

  // 🆕 NUOVO: Wrapper per toggleSection con scroll preservation
const toggleSection = useCallback((sectionId) => {
  const modalBody = scrollContainerRef.current;
  
  if (modalBody) {
    // Salva posizione corrente
    const currentScrollTop = modalBody.scrollTop;
    
    // Toggle la sezione
    originalToggleSection(sectionId);
    
    // Preserva lo scroll su mobile
    if (window.innerWidth <= 768) {
      requestAnimationFrame(() => {
        modalBody.scrollTop = currentScrollTop;
      });
    }
  } else {
    // Fallback se non c'è ref
    originalToggleSection(sectionId);
  }
}, [originalToggleSection, scrollContainerRef]);

// 🆕 NUOVO: Wrapper per expandAllSections con scroll preservation
const handleExpandAllSections = useCallback(() => {
  const modalBody = scrollContainerRef.current;
  const currentScrollTop = modalBody?.scrollTop || 0;
  
  expandAllSections();
  
  // Mantieni scroll su mobile
  if (window.innerWidth <= 768 && modalBody) {
    requestAnimationFrame(() => {
      modalBody.scrollTop = currentScrollTop;
    });
  }
}, [expandAllSections, scrollContainerRef]);

// 🆕 NUOVO: Wrapper per collapseAllSections con scroll reset
const handleCollapseAllSections = useCallback(() => {
  const modalBody = scrollContainerRef.current;
  
  collapseAllSections();
  
  // Scroll al top su mobile dopo chiusura
  if (window.innerWidth <= 768 && modalBody) {
    requestAnimationFrame(() => {
      modalBody.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    });
  }
}, [collapseAllSections, scrollContainerRef]);

  // 🆕 NUOVO: Determina gli utenti attivi dai rapportini esistenti
  const activeUsers = useMemo(() => {
    const users = new Set();
    
    rapportini.forEach(rapportino => {
      if (rapportino.personalePresente) {
        rapportino.personalePresente.forEach(persona => {
          if (persona.personaId) {
            users.add(persona.personaId);
          }
        });
      }
    });
    
    return Array.from(users);
  }, [rapportini]);
  
  // 🆕 NUOVO: Attiva la sincronizzazione automatica
  const { forceSyncAll, activeListeners } = useClientSideWorkHoursSync(activeUsers);

  // Caricamento dati iniziale
  useEffect(() => {
    loadRapportini();
    loadSupportData();
  }, [cantiere.id]);

  // Caricamento rapportini
  const loadRapportini = async () => {
    try {
      const data = await getRapportiniByCantiereId(cantiere.id);
      console.log('Raw rapportini data:', data); // Debug
      
      // Processa i dati senza convertire le date (lascia il formato originale)
      const processedData = data.map(rapportino => ({
        ...rapportino,
        // Non convertire le date qui, lascia il formato originale
        // Le convertiamo solo quando servono per il rendering o l'editing
      }));
      
      // Ordinamento sicuro delle date
      const sortedData = processedData.sort((a, b) => {
        try {
          const dateA = new Date(a.data);
          const dateB = new Date(b.data);
          return dateB - dateA;
        } catch (error) {
          console.error('Errore nell\'ordinamento date:', error);
          return 0;
        }
      });
      
      setRapportini(sortedData);
      
      // Seleziona automaticamente il primo rapportino se disponibile
      if (sortedData.length > 0 && !selectedRapportino) {
        handleSelectRapportino(sortedData[0]);
      }
    } catch (error) {
      console.error('Errore nel caricamento rapportini:', error);
      showNotification('Errore nel caricamento dei rapportini', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Funzione per ottenere le date disponibili per la copia mezzi
const getAvailableDatesWithMezzi = useCallback(() => {
  if (!rapportini || rapportini.length === 0) return [];
  
  // Filtra solo rapportini con mezzi utilizzati
  const rapportiniConMezzi = rapportini.filter(r => 
    r.mezziUtilizzati && 
    r.mezziUtilizzati.length > 0 &&
    r.data !== formData.data // Escludi la data corrente
  );
  
  // Ordina per data decrescente (più recenti prima)
  return rapportiniConMezzi
    .sort((a, b) => {
      const dateA = new Date(timestampToDateInput(a.data));
      const dateB = new Date(timestampToDateInput(b.data));
      return dateB - dateA;
    })
    .map(r => ({
      data: timestampToDateInput(r.data),
      id: r.id,
      mezziUtilizzati: r.mezziUtilizzati,
      displayDate: new Date(timestampToDateInput(r.data)).toLocaleDateString('it-IT', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }),
      numeroMezzi: r.mezziUtilizzati.length
    }));
}, [rapportini, formData.data]);

// Funzione per aprire il modal di copia mezzi
const handleOpenCopyMezziModal = () => {
  const dates = getAvailableDatesWithMezzi();
  
  if (dates.length === 0) {
    showNotification('Non ci sono rapportini precedenti con mezzi da copiare', 'warning');
    return;
  }
  
  setAvailableDatesForCopy(dates);
  setSelectedDateForCopy('');
  setShowCopyMezziModal(true);
};

// Funzione per copiare i mezzi da un rapportino precedente
const handleCopyMezziFromDate = () => {
  if (!selectedDateForCopy) {
    showNotification('Seleziona una data da cui copiare i mezzi', 'warning');
    return;
  }
  
  const rapportinoSelected = availableDatesForCopy.find(r => r.data === selectedDateForCopy);
  
  if (!rapportinoSelected || !rapportinoSelected.mezziUtilizzati) {
    showNotification('Rapportino non trovato o senza mezzi', 'error');
    return;
  }
  
  // Mezzi già presenti nel rapportino corrente
  const mezziAttuali = formData.mezziUtilizzati || [];
  const mezziAttualiIds = new Set(mezziAttuali.map(m => m.mezzoId));
  
  // Filtra solo i mezzi che non sono già presenti
  const mezziDaCopiare = rapportinoSelected.mezziUtilizzati.filter(
    mezzo => !mezziAttualiIds.has(mezzo.mezzoId)
  );
  
  if (mezziDaCopiare.length === 0) {
    showNotification('Tutti i mezzi del rapportino selezionato sono già presenti', 'info');
    setShowCopyMezziModal(false);
    return;
  }
  
  // Copia i mezzi con i loro dati
  const nuoviMezzi = mezziDaCopiare.map(mezzo => ({
    mezzoId: mezzo.mezzoId,
    nomeMezzo: mezzo.nomeMezzo,
    targa: mezzo.targa || '',
    ore: mezzo.ore || mezzo.oreLavorate || 0,
    oreLavorate: mezzo.ore || mezzo.oreLavorate || 0,
    costoOrario: mezzo.costoOrario || 0,
    totale: (mezzo.ore || mezzo.oreLavorate || 0) * (mezzo.costoOrario || 0)
  }));
  
  // Aggiorna formData con i nuovi mezzi
  const mezziAggiornati = [...mezziAttuali, ...nuoviMezzi];
  
  setFormData(prev => ({
    ...prev,
    mezziUtilizzati: mezziAggiornati
  }));
  
  // Chiudi il modal
  setShowCopyMezziModal(false);
  
  // Mostra notifica di successo
  const displayDate = new Date(selectedDateForCopy).toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  
  showNotification(
    `✅ Copiati ${nuoviMezzi.length} mezzi dal rapportino del ${displayDate}`,
    'success'
  );

  // ========================================
// FUNZIONI PER CREAZIONE RAPIDA MATERIALI
// ========================================

/**
 * Apre il modal per creare un nuovo materiale variabile
 */
const handleOpenNewMaterialeVariabileModal = () => {
  setNewMaterialeVariabileData({ nomeMacrovoce: '' });
  setShowNewMaterialeVariabileModal(true);
};

/**
 * Apre il modal per creare un nuovo materiale fisso
 */
const handleOpenNewMaterialeFissoModal = () => {
  setNewMaterialeFissoData({
    nomeMateriale: '',
    prezzoUnitario: '',
    unitaMisura: 'pz'
  });
  setShowNewMaterialeFissoModal(true);
};

/**
 * Crea un nuovo materiale variabile
 */
const handleCreateMaterialeVariabile = async () => {
  // Validazione
  if (!newMaterialeVariabileData.nomeMacrovoce.trim()) {
    showNotification('Inserisci il nome del materiale', 'error');
    return;
  }

  setIsCreatingMateriale(true);

  try {
    // Crea il materiale nel database
    const newMaterialeId = await createMaterialeCostoVariabile({
      nomeMacrovoce: newMaterialeVariabileData.nomeMacrovoce.trim()
    });

    console.log('✅ Nuovo materiale variabile creato:', newMaterialeId);

    // Ricarica la lista materiali
    const materialiAggiornati = await getMaterialiCostoVariabile();
    setMaterialiVariabili(materialiAggiornati);

    // Chiudi il modal
    setShowNewMaterialeVariabileModal(false);

    // Mostra notifica di successo
    showNotification(
      `✅ Materiale "${newMaterialeVariabileData.nomeMacrovoce}" creato con successo!`,
      'success'
    );

    // Seleziona automaticamente il materiale appena creato
    // Aspetta un momento per dare tempo al state di aggiornarsi
    setTimeout(() => {
      const nuovoMateriale = materialiAggiornati.find(m => m.id === newMaterialeId);
      if (nuovoMateriale) {
        // Qui potresti volere selezionare automaticamente il materiale
        // nella sezione MaterialiVariabiliSection
        console.log('Materiale creato e disponibile:', nuovoMateriale);
      }
    }, 100);

  } catch (error) {
    console.error('❌ Errore nella creazione materiale variabile:', error);
    showNotification(
      `❌ Errore: ${error.message}`,
      'error'
    );
  } finally {
    setIsCreatingMateriale(false);
  }
};

/**
 * Crea un nuovo materiale fisso
 */
const handleCreateMaterialeFisso = async () => {
  // Validazione
  if (!newMaterialeFissoData.nomeMateriale.trim()) {
    showNotification('Inserisci il nome del materiale', 'error');
    return;
  }

  if (!newMaterialeFissoData.prezzoUnitario || parseFloat(newMaterialeFissoData.prezzoUnitario) <= 0) {
    showNotification('Inserisci un prezzo unitario valido', 'error');
    return;
  }

  if (!newMaterialeFissoData.unitaMisura.trim()) {
    showNotification('Inserisci l\'unità di misura', 'error');
    return;
  }

  setIsCreatingMateriale(true);

  try {
    // Crea il materiale nel database
    const newMaterialeId = await createMaterialeCostoFisso({
      nomeMateriale: newMaterialeFissoData.nomeMateriale.trim(),
      prezzoUnitario: parseFloat(newMaterialeFissoData.prezzoUnitario),
      unitaMisura: newMaterialeFissoData.unitaMisura.trim()
    });

    console.log('✅ Nuovo materiale fisso creato:', newMaterialeId);

    // Ricarica la lista materiali
    const materialiAggiornati = await getMaterialiCostoFisso();
    setMaterialiFissi(materialiAggiornati);

    // Chiudi il modal
    setShowNewMaterialeFissoModal(false);

    // Mostra notifica di successo
    showNotification(
      `✅ Materiale "${newMaterialeFissoData.nomeMateriale}" creato con successo!`,
      'success'
    );

    // Seleziona automaticamente il materiale appena creato
    setTimeout(() => {
      const nuovoMateriale = materialiAggiornati.find(m => m.id === newMaterialeId);
      if (nuovoMateriale) {
        console.log('Materiale creato e disponibile:', nuovoMateriale);
      }
    }, 100);

  } catch (error) {
    console.error('❌ Errore nella creazione materiale fisso:', error);
    showNotification(
      `❌ Errore: ${error.message}`,
      'error'
    );
  } finally {
    setIsCreatingMateriale(false);
  }
};
  
  // Se siamo in modalità editing, aggiorna anche il database
  if (editingRapportino) {
    handleQuickUpdate('mezziUtilizzati', mezziAggiornati);
  }
};

  // Caricamento dati di supporto
  const loadSupportData = async () => {
    try {
      const [usersData, mezziData, matVariabiliData, matFissiData, lavorazioniData] = await Promise.all([
        getAllUsers(),
        getMezziAttrezzature(),
        getMaterialiCostoVariabile(),
        getMaterialiCostoFisso(),
        getLavorazioni()
      ]);
      
      setUsers(usersData);
      setMezzi(mezziData);
      setMaterialiVariabili(matVariabiliData);
      setMaterialiFissi(matFissiData);
      setLavorazioni(lavorazioniData);
      
      // Carica i nomi utenti
      await loadUserNames(usersData);
    } catch (error) {
      console.error('Errore nel caricamento dati di supporto:', error);
    }
  };

  // 🆕 NUOVO: Callback per ricaricare i rapportini dopo la distribuzione
const handleRapportinoUpdate = async () => {
  console.log('🔄 Ricaricamento rapportini dopo distribuzione');
  await loadRapportini();
};

  // 🆕 NUOVO: Sincronizzazione manuale per il cantiere
  const handleSyncCantiere = async () => {
    console.log('🔄 [CLIENT] Avvio sincronizzazione manuale cantiere');
    setSyncStatus('syncing');
    
    try {
      await forceSyncAll();
      setSyncStatus('success');
      
      // Ricarica i rapportini
      await loadRapportini();
      
      showNotification('🔄 Sincronizzazione completata con successo!', 'success');
      setTimeout(() => setSyncStatus(null), 3000);
      
    } catch (error) {
      console.error('❌ [CLIENT] Errore sincronizzazione cantiere:', error);
      setSyncStatus('error');
      showNotification('❌ Errore durante la sincronizzazione', 'error');
      setTimeout(() => setSyncStatus(null), 3000);
    }
  };
  
  // 🆕 NUOVO: Sincronizzazione per rapportino specifico
  const handleSyncRapportino = async (rapportino) => {
    if (!rapportino || !rapportino.personalePresente) return;
    
    console.log('🔄 [CLIENT] Sincronizzazione rapportino specifico:', rapportino.id);
    setSyncStatus('syncing');
    
    try {
      const dateStr = rapportino.data instanceof Date ? 
        rapportino.data.toISOString().split('T')[0] : 
        (typeof rapportino.data === 'string' ? rapportino.data : new Date(rapportino.data).toISOString().split('T')[0]);
      
      for (const persona of rapportino.personalePresente) {
        if (persona.personaId) {
          await syncRapportiniAfterWorkHoursChange(persona.personaId, dateStr);
        }
      }
      
      setSyncStatus('success');
      await loadRapportini();
      
      showNotification('🔄 Rapportino sincronizzato con successo!', 'success');
      setTimeout(() => setSyncStatus(null), 3000);
      
    } catch (error) {
      console.error('❌ [CLIENT] Errore sincronizzazione rapportino:', error);
      setSyncStatus('error');
      showNotification('❌ Errore sincronizzazione rapportino', 'error');
      setTimeout(() => setSyncStatus(null), 3000);
    }
  };

  // Funzione per caricare i nomi utenti
  const loadUserNames = async (usersData) => {
    try {
      const names = {};
      for (const user of usersData) {
        if (user.nome && user.cognome) {
          names[user.id] = `${user.nome} ${user.cognome}`;
        } else {
          names[user.id] = user.email || 'Utente sconosciuto';
        }
      }
      setUserNames(names);
    } catch (error) {
      console.error('Errore nel caricamento nomi utenti:', error);
    }
  };

  // Funzione helper per ottenere il nome utente (sincrona)
  const getUserName = (userId) => {
    if (!userId) return 'Utente sconosciuto';
    
    // Cerca nei dati utenti caricati
    const user = users.find(u => u.id === userId);
    if (user) {
      if (user.nome && user.cognome) {
        return `${user.nome} ${user.cognome}`;
      }
      return user.email || 'Utente sconosciuto';
    }
    
    return userNames[userId] || 'Caricamento...';
  };

  // Funzione helper per ottenere il nome persona dal personalePresente
  const getPersonaName = (persona) => {
    if (!persona) return 'Persona sconosciuta';
    
    // Prima controlla se ha nome e cognome direttamente nell'oggetto persona
    if (persona.nome && persona.cognome) {
      return `${persona.nome} ${persona.cognome}`;
    }
    
    // Poi cerca nei dati utenti usando personaId
    if (persona.personaId) {
      const user = users.find(u => u.id === persona.personaId);
      if (user) {
        if (user.nome && user.cognome) {
          return `${user.nome} ${user.cognome}`;
        }
        return user.email || 'Utente sconosciuto';
      }
    }
    
    // Fallback con userId se presente
    if (persona.userId) {
      return getUserName(persona.userId);
    }
    
    return 'Persona sconosciuta';
  };

  // Funzione helper per formattare la data del rapportino
  const formatRapportinoDate = (dataField) => {
    if (!dataField) return 'Data non disponibile';
    
    try {
      let date;
      
      // Se è un Timestamp Firebase
      if (dataField && typeof dataField.toDate === 'function') {
        date = dataField.toDate();
      }
      // Se è un oggetto con seconds (Timestamp serializzato)
      else if (dataField && typeof dataField === 'object' && dataField.seconds) {
        date = new Date(dataField.seconds * 1000);
      }
      // Se è già un oggetto Date
      else if (dataField instanceof Date) {
        date = dataField;
      }
      // Se è una stringa
      else if (typeof dataField === 'string') {
        date = new Date(dataField);
      }
      // Se è un numero (timestamp)
      else if (typeof dataField === 'number') {
        date = new Date(dataField);
      }
      // Fallback per oggetti
      else if (typeof dataField === 'object') {
        // Prova a convertire l'oggetto in stringa prima
        date = new Date(dataField.toString());
      }
      // Fallback finale
      else {
        date = new Date(dataField);
      }
      
      // Verifica che la data sia valida
      if (date && !isNaN(date.getTime())) {
        return date.toLocaleDateString('it-IT');
      } else {
        return 'Data non valida';
      }
    } catch (error) {
      console.error('Errore nella formattazione data rapportino:', error, 'Valore:', dataField);
      return 'Data non valida';
    }
  };

  // Funzione helper per convertire timestamp Firebase in stringa data per input
  const timestampToDateInput = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      let date;
      
      // Se è un Timestamp Firebase
      if (timestamp && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      } 
      // Se è un oggetto con seconds (Timestamp serializzato)
      else if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } 
      // Se è già un oggetto Date
      else if (timestamp instanceof Date) {
        date = timestamp;
      } 
      // Se è una stringa ISO
      else if (typeof timestamp === 'string') {
        // Se è già nel formato corretto per input date
        if (/^\d{4}-\d{2}-\d{2}$/.test(timestamp)) {
          return timestamp;
        }
        date = new Date(timestamp);
      } 
      // Se è un numero (timestamp Unix)
      else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      } 
      // Se è un altro tipo di oggetto
      else if (typeof timestamp === 'object') {
        // Prova a convertire l'oggetto
        date = new Date(timestamp.toString());
      } 
      // Fallback finale
      else {
        date = new Date(timestamp);
      }
      
      // Verifica che la data sia valida e convertila
      if (date && !isNaN(date.getTime())) {
        const isoString = date.toISOString();
        return isoString.split('T')[0];
      } else {
        console.warn('Data non valida per timestampToDateInput:', timestamp);
        return '';
      }
    } catch (error) {
      console.error('Errore nella conversione timestamp:', error, 'Valore:', timestamp);
      return '';
    }
  };

  // Funzione helper per ottenere il nome del mezzo
  const getMezzoName = (mezzoId) => {
    if (!mezzoId) return 'Mezzo sconosciuto';
    const mezzo = mezzi.find(m => m.id === mezzoId);
    return mezzo ? (mezzo.nomeMezzo || mezzo.nome || 'Mezzo senza nome') : 'Mezzo non trovato';
  };

  // Funzione helper per ottenere il nome del materiale variabile
  const getMaterialeVariabileName = (materialeId) => {
    if (!materialeId) return 'Materiale sconosciuto';
    const materiale = materialiVariabili.find(m => m.id === materialeId);
    return materiale ? (materiale.nomeMacrovoce || materiale.nome || 'Materiale senza nome') : 'Materiale non trovato';
  };

  // Funzione helper per ottenere il nome del materiale fisso
  const getMaterialeFissoName = (materialeId) => {
    if (!materialeId) return 'Materiale sconosciuto';
    const materiale = materialiFissi.find(m => m.id === materialeId);
    return materiale ? (materiale.nomeMateriale || materiale.nome || 'Materiale senza nome') : 'Materiale non trovato';
  };

  // Funzione helper per ottenere il nome della lavorazione
  const getLavorazioneName = (lavorazioneId) => {
    if (!lavorazioneId) return 'Lavorazione sconosciuta';
    const lavorazione = lavorazioni.find(l => l.id === lavorazioneId);
    return lavorazione ? (lavorazione.nomeLavorazione || lavorazione.nome || 'Lavorazione senza nome') : 'Lavorazione non trovata';
  };

  // Funzione per mostrare notifiche
  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
  };

  // useEffect per il filtro rapportini
useEffect(() => {
  const filtered = filterRapportini(rapportini, searchTerm);
  setFilteredRapportini(filtered);
}, [rapportini, searchTerm]);

// useEffect per rilevare mobile
useEffect(() => {
  const checkMobile = () => {
    setIsMobile(window.innerWidth <= 768);
  };
  
  checkMobile();
  window.addEventListener('resize', checkMobile);
  return () => window.removeEventListener('resize', checkMobile);
}, []);

 // ✅ AGGIUNGI QUESTO QUI - NUOVO useEffect per data-label tabelle mobile
 useEffect(() => {
  // Solo su dispositivi mobile
  if (window.innerWidth <= 768) {
    // Aspetta che il DOM sia renderizzato
    setTimeout(() => {
      document.querySelectorAll('.table td').forEach(td => {
        const index = Array.from(td.parentElement.children).indexOf(td);
        const th = td.closest('table')?.querySelector(`thead th:nth-child(${index + 1})`);
        if (th) {
          td.setAttribute('data-label', th.textContent);
        }
      });
    }, 100);
  }
}, [formData, selectedRapportino, showForm]); // ✅ Dipendenze corrette


            // Funzione per filtrare i rapportini
            const filterRapportini = (rapportini, searchTerm) => {
              if (!searchTerm.trim()) {
                return rapportini;
              }
              
              const searchLower = searchTerm.toLowerCase().trim();
              
              return rapportini.filter(rapportino => {
                // Ricerca per data formattata
                const dataFormatted = formatRapportinoDate(rapportino.data).toLowerCase();
                if (dataFormatted.includes(searchLower)) return true;
                
                // Ricerca per nome redattore
                const redattore = getUserName(rapportino.redattoDa).toLowerCase();
                if (redattore.includes(searchLower)) return true;
                
                // Ricerca per note varie
                if (rapportino.noteVarie && rapportino.noteVarie.toLowerCase().includes(searchLower)) return true;
                
                // Ricerca per personale presente
                if (rapportino.personalePresente && rapportino.personalePresente.some(persona => {
                  const nomePersona = getPersonaName(persona).toLowerCase();
                  return nomePersona.includes(searchLower);
                })) return true;
                
                // Ricerca per lavorazioni
                if (rapportino.lavorazioni && rapportino.lavorazioni.some(lavorazione => {
                  const nomeLavorazione = (lavorazione.nomeLavorazione || '').toLowerCase();
                  return nomeLavorazione.includes(searchLower);
                })) return true;
                
                return false;
              });
            };
            
// Sostituisci la funzione handleOpenSettings esistente con questa versione perfezionata:

const handleOpenSettings = (rapportinoId, event) => {
  event.stopPropagation();
  
  // ✅ AGGIUNTA: Trova e seleziona automaticamente il rapportino
  const rapportinoTarget = filteredRapportini.find(r => r.id === rapportinoId);
  if (rapportinoTarget) {
    handleSelectRapportino(rapportinoTarget);
  }
  
  if (!isMobile) {
    // 🎯 CALCOLO PERFETTO: Usa solo getBoundingClientRect senza scroll
    const rect = event.currentTarget.getBoundingClientRect();
    
    console.log('🎯 Posizionamento pulsante:', {
      rect: {
        top: rect.top,
        left: rect.left,
        bottom: rect.bottom,
        right: rect.right,
        width: rect.width,
        height: rect.height
      },
      viewport: { 
        width: window.innerWidth, 
        height: window.innerHeight 
      }
    });
    
    // 🔧 POSIZIONE PRECISA: Il dropdown si aprirà direttamente sotto il pulsante
    setDropdownPosition({
      top: rect.bottom + 4, // 4px di spazio dal pulsante
      left: rect.left        // Allineato al bordo sinistro del pulsante
    });
  }
  
  setOpenSettingsDropdown(rapportinoId);
};
            
            // Funzioni per le azioni del dropdown
            const handleModificaAvanzata = () => {
              if (selectedRapportino) {
                handleEdit(selectedRapportino);
              }
              setOpenSettingsDropdown(null);
            };
            
            const handleDuplicaRapportino = async () => {
              if (!selectedRapportino) {
                showNotification('⚠️ Nessun rapportino selezionato', 'warning');
                return;
              }
            
              // Apri modal per scegliere la data
              setRapportinoToDuplicate(selectedRapportino);
              setShowDateModal(true);
              setOpenSettingsDropdown(null);
            };
            
            // 4.4 AGGIUNGI handleDateConfirm (nuova funzione)
            const handleDateConfirm = async (selectedDate) => {
              setShowDateModal(false);
              
              if (!rapportinoToDuplicate) return;
            
              try {
                showNotification(
                  `⏳ Duplicazione per ${new Date(selectedDate).toLocaleDateString('it-IT')}...`,
                  'info'
                );
                
                const newRapportinoId = await duplicateRapportino(
                  rapportinoToDuplicate.id, 
                  cantiere.id,
                  selectedDate // 🆕 PASSA LA DATA SCELTA
                );
                
                console.log(`✅ Rapportino duplicato: ${newRapportinoId} per ${selectedDate}`);
                
                await loadRapportini();
                
                setTimeout(async () => {
                  const rapportiniAggiornati = await getRapportiniByCantiereId(cantiere.id);
                  const nuovoRapportino = rapportiniAggiornati.find(r => r.id === newRapportinoId);
                  if (nuovoRapportino) {
                    handleSelectRapportino(nuovoRapportino);
                  }
                }, 1000);
                
                showNotification(
                  `✅ Rapportino duplicato per ${new Date(selectedDate).toLocaleDateString('it-IT')}!`,
                  'success'
                );
                
              } catch (error) {
                console.error('❌ Errore:', error);
                showNotification(`❌ Errore: ${error.message}`, 'error');
              } finally {
                setRapportinoToDuplicate(null);
              }
            };
            
            // 4.5 AGGIUNGI handleDateCancel (nuova funzione)
            const handleDateCancel = () => {
              setShowDateModal(false);
              setRapportinoToDuplicate(null);
            };
            
            const handleEliminaRapportino = async () => {
              if (!selectedRapportino || !window.confirm('Sei sicuro di voler eliminare questo rapportino?')) return;
              
              try {
                await deleteRapportino(selectedRapportino.id);
                await loadRapportini();
                setSelectedRapportino(null);
                setFormData({});
                showNotification('Rapportino eliminato con successo', 'success');
              } catch (error) {
                console.error('Errore nell\'eliminazione:', error);
                showNotification('Errore nell\'eliminazione del rapportino', 'error');
              }
              setOpenSettingsDropdown(null);
            };

            // ========================================
// 🆕 FUNZIONI PER CREAZIONE RAPIDA MATERIALI
// ========================================
// POSIZIONA QUESTE FUNZIONI DOPO handleDateCancel (riga ~825)
// PRIMA di resetForm

/**
 * Apre il modal per creare un nuovo materiale variabile
 */
const handleOpenNewMaterialeVariabileModal = () => {
  setNewMaterialeVariabileData({ nomeMacrovoce: '' });
  setShowNewMaterialeVariabileModal(true);
};

/**
 * Apre il modal per creare un nuovo materiale fisso
 */
const handleOpenNewMaterialeFissoModal = () => {
  setNewMaterialeFissoData({
    nomeMateriale: '',
    prezzoUnitario: '',
    unitaMisura: 'pz'
  });
  setShowNewMaterialeFissoModal(true);
};

/**
 * Crea un nuovo materiale variabile
 */
const handleCreateMaterialeVariabile = async () => {
  // Validazione
  if (!newMaterialeVariabileData.nomeMacrovoce.trim()) {
    showNotification('Inserisci il nome del materiale', 'error');
    return;
  }

  setIsCreatingMateriale(true);

  try {
    // Crea il materiale nel database
    const newMaterialeId = await createMaterialeCostoVariabile({
      nomeMacrovoce: newMaterialeVariabileData.nomeMacrovoce.trim()
    });

    console.log('✅ Nuovo materiale variabile creato:', newMaterialeId);

    // Ricarica la lista materiali
    const materialiAggiornati = await getMaterialiCostoVariabile();
    setMaterialiVariabili(materialiAggiornati);

    // Chiudi il modal
    setShowNewMaterialeVariabileModal(false);

    // Mostra notifica di successo
    showNotification(
      `✅ Materiale "${newMaterialeVariabileData.nomeMacrovoce}" creato con successo!`,
      'success'
    );

    // Seleziona automaticamente il materiale appena creato
    setTimeout(() => {
      const nuovoMateriale = materialiAggiornati.find(m => m.id === newMaterialeId);
      if (nuovoMateriale) {
        console.log('Materiale creato e disponibile:', nuovoMateriale);
      }
    }, 100);

  } catch (error) {
    console.error('❌ Errore nella creazione materiale variabile:', error);
    showNotification(
      `❌ Errore: ${error.message}`,
      'error'
    );
  } finally {
    setIsCreatingMateriale(false);
  }
};

/**
 * Crea un nuovo materiale fisso
 */
const handleCreateMaterialeFisso = async () => {
  // Validazione
  if (!newMaterialeFissoData.nomeMateriale.trim()) {
    showNotification('Inserisci il nome del materiale', 'error');
    return;
  }

  if (!newMaterialeFissoData.prezzoUnitario || parseFloat(newMaterialeFissoData.prezzoUnitario) <= 0) {
    showNotification('Inserisci un prezzo unitario valido', 'error');
    return;
  }

  if (!newMaterialeFissoData.unitaMisura.trim()) {
    showNotification('Inserisci l\'unità di misura', 'error');
    return;
  }

  setIsCreatingMateriale(true);

  try {
    // Crea il materiale nel database
    const newMaterialeId = await createMaterialeCostoFisso({
      nomeMateriale: newMaterialeFissoData.nomeMateriale.trim(),
      prezzoUnitario: parseFloat(newMaterialeFissoData.prezzoUnitario),
      unitaMisura: newMaterialeFissoData.unitaMisura.trim()
    });

    console.log('✅ Nuovo materiale fisso creato:', newMaterialeId);

    // Ricarica la lista materiali
    const materialiAggiornati = await getMaterialiCostoFisso();
    setMaterialiFissi(materialiAggiornati);

    // Chiudi il modal
    setShowNewMaterialeFissoModal(false);

    // Mostra notifica di successo
    showNotification(
      `✅ Materiale "${newMaterialeFissoData.nomeMateriale}" creato con successo!`,
      'success'
    );

    // Seleziona automaticamente il materiale appena creato
    setTimeout(() => {
      const nuovoMateriale = materialiAggiornati.find(m => m.id === newMaterialeId);
      if (nuovoMateriale) {
        console.log('Materiale creato e disponibile:', nuovoMateriale);
      }
    }, 100);

  } catch (error) {
    console.error('❌ Errore nella creazione materiale fisso:', error);
    showNotification(
      `❌ Errore: ${error.message}`,
      'error'
    );
  } finally {
    setIsCreatingMateriale(false);
  }
};

  // Reset form
  const resetForm = () => {
    const cleanFormData = {
      data: new Date().toISOString().split('T')[0],
      redattoDa: currentUser?.uid || '',
      personalePresente: [],
      mezziUtilizzati: [],
      materialiVariabili: [],
      materialiFissi: [],
      lavorazioni: [],
      ricavi: [],
      speseVarie: [],  // 🆕 NUOVO
      noteVarie: '',
      allegati: []
    };


    
    setFormData(cleanFormData);
    setEditingRapportino(null);
    setShowForm(false);
  };

  // Calcolo totali rapportino
  const getTotaliRapportino = () => {
    const personalePresente = formData.personalePresente || [];
    const mezziUtilizzati = formData.mezziUtilizzati || [];
    const materialiVariabili = formData.materialiVariabili || [];
    const materialiFissi = formData.materialiFissi || [];
    const ricavi = formData.ricavi || [];
    const speseVarie = formData.speseVarie || [];  // 🆕 NUOVO
  
    // Calcolo base delle categorie esistenti
    let totalePersonale = personalePresente.reduce((sum, p) => sum + getCostoPersona(p), 0);
    let totaleMezzi = mezziUtilizzati.reduce((sum, m) => sum + parseFloat(m.totale || 0), 0);
    let totaleMatVariabili = materialiVariabili.reduce((sum, m) => sum + (m.costo || 0), 0);
    let totaleMatFissi = materialiFissi.reduce((sum, m) => sum + parseFloat(m.totale || 0), 0);
    const totaleRicavi = ricavi.reduce((sum, r) => sum + (r.importo || 0), 0);
  
    // 🆕 Aggiungi le spese varie alle categorie corrette
    let totaleSpeseVarie = 0;
    speseVarie.forEach(spesa => {
      const importo = parseFloat(spesa.importo || 0);
      
      switch(spesa.categoria) {
        case 'Personale':
          totalePersonale += importo;
          break;
        case 'Mezzi':
          totaleMezzi += importo;
          break;
        case 'Materiali Variabili':
          totaleMatVariabili += importo;
          break;
        case 'Materiali Fissi':
          totaleMatFissi += importo;
          break;
        case 'Spese Varie':
        default:
          totaleSpeseVarie += importo;
          break;
      }
    });
  
    // Calcolo totale costi includendo tutte le categorie
    const totaleCosti = totalePersonale + totaleMezzi + totaleMatVariabili + totaleMatFissi + totaleSpeseVarie;
    const margine = totaleRicavi - totaleCosti;
  
    return {
      // valori numerici grezzi
      totalePersonale,
      totaleMezzi,
      totaleMatVariabili,
      totaleMatFissi,
      totaleSpeseVarie,  // 🆕 NUOVO
      totaleCosti,
      totaleRicavi,
      margine,
  
      // valori formattati
      formato: {
        personale: formatEuro(totalePersonale),
        mezzi: formatEuro(totaleMezzi),
        materialiVariabili: formatEuro(totaleMatVariabili),
        materialiFissi: formatEuro(totaleMatFissi),
        speseVarie: formatEuro(totaleSpeseVarie),  // 🆕 NUOVO
        costi: formatEuro(totaleCosti),
        ricavi: formatEuro(totaleRicavi),
        margine: formatEuro(margine)
      }
    };
  };


  // Handlers per le azioni sui rapportini
  const handleSelectRapportino = (rapportino) => {
    console.log('Selecting rapportino:', rapportino);
    
    setSelectedRapportino(rapportino);
    
    // 🔧 FIX: Assicurati che l'editing rapportino sia impostato correttamente
    setEditingRapportino(rapportino);
  
    
    // Conversione sicura anche qui
    const safeFormData = {
      data: timestampToDateInput(rapportino.data) || new Date().toISOString().split('T')[0],
      redattoDa: rapportino.redattoDa || '',
      personalePresente: Array.isArray(rapportino.personalePresente) ? rapportino.personalePresente : [],
      mezziUtilizzati: Array.isArray(rapportino.mezziUtilizzati) ? rapportino.mezziUtilizzati : [],
      materialiVariabili: Array.isArray(rapportino.materialiVariabili) ? rapportino.materialiVariabili : [],
      materialiFissi: Array.isArray(rapportino.materialiFissi) ? rapportino.materialiFissi : [],
      lavorazioni: Array.isArray(rapportino.lavorazioni) ? rapportino.lavorazioni : [],
      ricavi: Array.isArray(rapportino.ricavi) ? rapportino.ricavi : [],
      speseVarie: Array.isArray(rapportino.speseVarie) ? rapportino.speseVarie : [],  // 🆕 AGGIUNGI
      noteVarie: typeof rapportino.noteVarie === 'string' ? rapportino.noteVarie : '',
      allegati: Array.isArray(rapportino.allegati) ? rapportino.allegati : [],
      modificatoDa: rapportino.modificatoDa || '',
      dataModifica: rapportino.dataModifica ? timestampToDateInput(rapportino.dataModifica) : ''
    };
    setFormData(safeFormData);
    setOpenSettingsDropdown(null);
  };

  const handleEdit = (rapportino) => {
    console.log('Editing rapportino:', rapportino); // Debug
    
    setEditingRapportino(rapportino);
    
    // Conversione sicura di tutti i campi
    const safeFormData = {
      data: timestampToDateInput(rapportino.data) || new Date().toISOString().split('T')[0],
      redattoDa: rapportino.redattoDa || currentUser?.uid || '',
      personalePresente: Array.isArray(rapportino.personalePresente) ? rapportino.personalePresente : [],
      mezziUtilizzati: Array.isArray(rapportino.mezziUtilizzati) ? rapportino.mezziUtilizzati : [],
      materialiVariabili: Array.isArray(rapportino.materialiVariabili) ? rapportino.materialiVariabili : [],
      materialiFissi: Array.isArray(rapportino.materialiFissi) ? rapportino.materialiFissi : [],
      lavorazioni: Array.isArray(rapportino.lavorazioni) ? rapportino.lavorazioni : [],
      ricavi: Array.isArray(rapportino.ricavi) ? rapportino.ricavi : [],
      speseVarie: Array.isArray(rapportino.speseVarie) ? rapportino.speseVarie : [],  // 🆕 AGGIUNGI
      noteVarie: typeof rapportino.noteVarie === 'string' ? rapportino.noteVarie : '',
      allegati: Array.isArray(rapportino.allegati) ? rapportino.allegati : [],
      createdAt: rapportino.createdAt ? timestampToDateInput(rapportino.createdAt) : '',
      updatedAt: rapportino.updatedAt ? timestampToDateInput(rapportino.updatedAt) : '',
      modificatoDa: rapportino.modificatoDa || '',
      dataModifica: rapportino.dataModifica ? timestampToDateInput(rapportino.dataModifica) : ''
    };
    
    console.log('Safe form data:', safeFormData); // Debug
    
    setFormData(safeFormData);
    setShowForm(true);
    setOpenSettingsDropdown(null);
  };

  // Form submission handler
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Validazioni manuali
    if (!formData.data) {
      showNotification('La data è obbligatoria', 'error');
      return;
    }

    if (new Date(formData.data) > new Date()) {
      showNotification('Non è possibile inserire una data futura', 'error');
      return;
    }

// Validazione: almeno una sezione deve essere compilata
const hasPersonale = formData.personalePresente?.length > 0;
const hasMezzi = formData.mezziUtilizzati?.length > 0;
const hasMateriali = (formData.materialiVariabili?.length > 0) || (formData.materialiFissi?.length > 0);
const hasLavorazioni = formData.lavorazioni?.length > 0;
const hasRicavi = formData.ricavi?.length > 0;
const hasSpeseVarie = formData.speseVarie?.length > 0;  // 🆕 NUOVO

if (!hasPersonale && !hasMezzi && !hasMateriali && !hasLavorazioni && !hasRicavi && !hasSpeseVarie) {
  showNotification('Inserire almeno una sezione del rapportino', 'error');
  return;
}

    try {
      const rapportinoData = {
        ...formData,
        cantiereId: cantiere.id,
        nomeRapportino: formData.data,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: currentUser.uid
      };

      if (editingRapportino) {
        rapportinoData.modificatoDa = currentUser.uid;
        rapportinoData.modificatoIl = new Date();
        rapportinoData.modifiche = [
          ...(editingRapportino.modifiche || []),
          {
            data: new Date(),
            utente: currentUser.uid,
            nomeUtente: `${currentUser.nome} ${currentUser.cognome}` || currentUser.email
          }
        ];

        await updateRapportino(editingRapportino.id, rapportinoData);
        showNotification('Rapportino aggiornato con successo', 'success');
      } else {
        await createRapportino(rapportinoData);
        showNotification('Rapportino creato con successo', 'success');
      }

      // 🆕 AGGIUNGERE QUESTO BLOCCO:
try {
  const syncResult = await syncPendingManualHours(
    formData.personalePresente, 
    formData.data
  );
  
  if (syncResult.syncedCount > 0) {
    console.log(`✅ ${syncResult.syncedCount} ore manuali sincronizzate`);
  }
  
  if (syncResult.errorCount > 0) {
    showNotification(
      editingRapportino 
        ? 'Rapportino aggiornato, ma alcune ore non sincronizzate' 
        : 'Rapportino creato, ma alcune ore non sincronizzate',
      'warning'
    );
  } else {
    showNotification(
      editingRapportino 
        ? 'Rapportino aggiornato con successo' 
        : 'Rapportino creato con successo',
      'success'
    );
  }
} catch (syncError) {
  console.error('Errore sincronizzazione:', syncError);
  showNotification(
    editingRapportino 
      ? 'Rapportino aggiornato, errore sincronizzazione ore' 
      : 'Rapportino creato, errore sincronizzazione ore',
    'warning'
  );
}

      resetForm();
      await loadRapportini();

      // 🆕 SISTEMA AUTOMATICO: Distribuzione automatica dopo salvataggio
if (formData.personalePresente && formData.personalePresente.length > 0) {
  const userIds = formData.personalePresente
    .filter(p => p.statoPresenza === 'presente' && p.personaId)
    .map(p => p.personaId);
  
  if (userIds.length > 0) {
    console.log(`🔄 [ADMIN] Avvio distribuzione automatica per ${userIds.length} persone`);
    
    setTimeout(async () => {
      try {
        const results = await triggerBatchDistribution(userIds, formData.data);
        
        if (results.successful > 0) {
          console.log(`✅ [ADMIN] Distribuzione automatica completata: ${results.successful}/${results.total} persone`);
          await loadRapportini();
          
          showNotification(
            `Rapportino salvato e ore distribuite automaticamente per ${results.successful} persone`, 
            'success'
          );
        } else if (results.errors > 0) {
          showNotification(
            `Rapportino salvato ma alcune distribuzioni hanno avuto errori`, 
            'warning'
          );
        }
      } catch (error) {
        console.error('❌ [ADMIN] Errore distribuzione automatica post-salvataggio:', error);
        showNotification(
          'Rapportino salvato ma errore nella distribuzione automatica', 
          'warning'
        );
      }
    }, 2000);
  }
}

    // 🆕 AGGIUNGERE QUESTO CODICE QUI - DOPO loadRapportini():
    // 🆕 NUOVO: Dopo il salvataggio, avvia la distribuzione per ogni persona
    if (formData.personalePresente && formData.personalePresente.length > 0) {
      for (const persona of formData.personalePresente) {
        if (persona.personaId && persona.statoPresenza === 'presente') {
          // Avvia la distribuzione con un piccolo ritardo
          setTimeout(async () => {
            try {
              // Ricarica i rapportini per vedere le modifiche
              await loadRapportini();
            } catch (error) {
              console.error('Errore nella distribuzione automatica:', error);
            }
          }, 1000);
        }
      }
    }

  } catch (error) {
    console.error('Errore nel salvataggio:', error);
    showNotification(error.message || 'Errore nel salvataggio del rapportino', 'error');
  }
};

  // Aggiornamento rapido dei dati
  const handleQuickUpdate = async (section, data) => {
    if (!selectedRapportino) return;
    
    setIsUpdating(true);
    try {
      // Prima aggiorna il formData locale immediatamente
      const updatedFormData = {
        ...formData,
        [section]: data
      };
      setFormData(updatedFormData);
      
      // Poi aggiorna il database
      const updatedData = {
        ...selectedRapportino,
        [section]: data,
        modificatoDa: currentUser.uid,
        dataModifica: new Date().toISOString()
      };
      
      await updateRapportino(selectedRapportino.id, updatedData);

      // 🆕 SISTEMA AUTOMATICO: Distribuzione per aggiornamenti rapidi
if (section === 'personalePresente' && Array.isArray(data)) {
  const userIds = data
    .filter(p => p.statoPresenza === 'presente' && p.personaId)
    .map(p => p.personaId);
  
  if (userIds.length > 0) {
    console.log(`🔄 [ADMIN] Distribuzione automatica per aggiornamento rapido: ${userIds.length} persone`);
    
    setTimeout(async () => {
      try {
        const results = await triggerBatchDistribution(userIds, formData.data);
        
        if (results.successful > 0) {
          console.log(`✅ [ADMIN] Distribuzione rapida completata: ${results.successful} persone`);
          await loadRapportini();
        }
      } catch (error) {
        console.error('❌ [ADMIN] Errore distribuzione rapida:', error);
      }
    }, 1500);
  }
}
      
      // Ricarica la lista rapportini
      await loadRapportini();
      
      // Trova e aggiorna il rapportino selezionato
      const updatedRapportini = await getRapportiniByCantiereId(cantiere.id);
      const updatedSelected = updatedRapportini.find(r => r.id === selectedRapportino.id);
      if (updatedSelected) {
        setSelectedRapportino(updatedSelected);
      }
      
      showNotification('Rapportino aggiornato con successo', 'success');
    } catch (error) {
      console.error('Errore nell\'aggiornamento:', error);
      showNotification('Errore nell\'aggiornamento del rapportino', 'error');
      // Ripristina il formData precedente in caso di errore
      setFormData(selectedRapportino);
    } finally {
      setIsUpdating(false);
    }
  };

  // Funzioni per gestire le selezioni dai suggerimenti
  const handleSelectPersona = (user) => {
    // Controllo anti-doppioni
    const personalePresente = formData.personalePresente || [];
    const giaPresente = personalePresente.some(p => 
      p.personaId === user.id || p.userId === user.id
    );
    
    if (giaPresente) {
      showNotification('Questa persona è già presente nel rapportino', 'error');
      return;
    }
    
    const newPersona = {
      personaId: user.id,
      userId: user.id,
      nome: user.nome,
      cognome: user.cognome,
      incarico: user.incarico || 'Operaio',
      oreLavorate: 0,
      costoOrario: user.costoOrario || 0,
      totale: 0,
      statoPresenza: 'presente'
    };
    const newPersonale = [...personalePresente, newPersona];
    
    // Aggiorna immediatamente formData
    setFormData(prev => ({
      ...prev,
      personalePresente: newPersonale
    }));
    
    // Poi aggiorna il database
    handleQuickUpdate('personalePresente', newPersonale);
    setSearchPersonale('');
  };

  const handleSelectMezzo = (mezzo) => {
    // Controllo anti-doppioni
    const mezziUtilizzati = formData.mezziUtilizzati || [];
    const giaPresente = mezziUtilizzati.some(m => m.mezzoId === mezzo.id);
    
    if (giaPresente) {
      showNotification('Questo mezzo è già presente nel rapportino', 'error');
      return;
    }
    
    const newMezzo = {
      mezzoId: mezzo.id,
      nomeMezzo: mezzo.nomeMezzo || mezzo.nome,
      targa: mezzo.targa || '',
      ore: 0,
      costoOrario: mezzo.costoOrario || 0,
      totale: 0
    };
    const newMezzi = [...mezziUtilizzati, newMezzo];
    
    // Aggiorna immediatamente formData
    setFormData(prev => ({
      ...prev,
      mezziUtilizzati: newMezzi
    }));
    
    // Poi aggiorna il database
    handleQuickUpdate('mezziUtilizzati', newMezzi);
    setSearchMezzi('');
  };

  const handleSelectMaterialeVariabile = (materiale) => {
    const materialiVariabiliPresenti = formData.materialiVariabili || [];
    const giaPresente = materialiVariabiliPresenti.some(m => m.materialeId === materiale.id);
    
    if (giaPresente) {
      showNotification('Questo materiale è già presente nel rapportino', 'error');
      return;
    }
    
    const newMateriale = {
      materialeId: materiale.id,
      nomeMacrovoce: materiale.nomeMacrovoce || materiale.nome,
      descrizione: '',
      costo: 0
    };
    const newMateriali = [...materialiVariabiliPresenti, newMateriale];
    
    setFormData(prev => ({
      ...prev,
      materialiVariabili: newMateriali
    }));
    
    // ✅ CORREZIONE: Usa le variabili giuste per i materiali variabili
    handleQuickUpdate('materialiVariabili', newMateriali);
    setSearchMatVariabili('');
  };

  const handleSelectMaterialeFisso = (materiale) => {
    const materialiFissiPresenti = formData.materialiFissi || [];
    const giaPresente = materialiFissiPresenti.some(m => m.materialeId === materiale.id);
    
    if (giaPresente) {
      showNotification('Questo materiale è già presente nel rapportino', 'error');
      return;
    }
    
    const prezzoBase = parseFloat(materiale.prezzoUnitario || 0);
    const costoTrasporto = parseFloat(cantiere.variazioneCostiMateriali || 0);
    const prezzoTotale = prezzoBase + costoTrasporto;
    
    const newMateriale = {
      materialeId: materiale.id,
      nomeMateriale: materiale.nomeMateriale || materiale.nome,
      unitaMisura: materiale.unitaMisura || 'pz',
      quantita: 0,
      prezzoUnitarioBase: prezzoBase,
      costoTrasporto: costoTrasporto,
      prezzoUnitario: prezzoTotale,
      totale: 0
    };
    const newMateriali = [...materialiFissiPresenti, newMateriale];
    
    setFormData(prev => ({ ...prev, materialiFissi: newMateriali }));
    handleQuickUpdate('materialiFissi', newMateriali);
    setSearchMatFissi('');
  };

  const handleSelectLavorazione = (lavorazione) => {
    const lavorazioniPresenti = formData.lavorazioni || [];
    const giaPresente = lavorazioniPresenti.some(l => l.lavorazioneId === lavorazione.id);
    
    if (giaPresente) {
      showNotification('Questa lavorazione è già presente nel rapportino', 'error');
      return;
    }
    
    const newLavorazione = {
      lavorazioneId: lavorazione.id,
      nomeLavorazione: lavorazione.nomeLavorazione || lavorazione.nome,
      descrizione: '',
      note: '',
      isCustom: false
    };
    const newLavorazioni = [...lavorazioniPresenti, newLavorazione];
    
    setFormData(prev => ({ ...prev, lavorazioni: newLavorazioni }));
    handleQuickUpdate('lavorazioni', newLavorazioni);
    setSearchLavorazioni('');
  };

  // Funzioni per filtrare i suggerimenti disponibili
  const getAvailableUsers = () => {
    return users.filter(u => !formData.personalePresente?.some(p => 
      p.personaId === u.id || p.userId === u.id
    ));
  };

  const getAvailableMezzi = () => {
    return mezzi.filter(m => !formData.mezziUtilizzati?.some(mu => mu.mezzoId === m.id));
  };

  const getAvailableMaterialiVariabili = () => {
    return materialiVariabili.filter(m => !formData.materialiVariabili?.some(mv => mv.materialeId === m.id));
  };

  const getAvailableMaterialiFissi = () => {
    return materialiFissi.filter(m => !formData.materialiFissi?.some(mf => mf.materialeId === m.id));
  };

  const getAvailableLavorazioni = () => {
    return lavorazioni.filter(l => !formData.lavorazioni?.some(fl => fl.lavorazioneId === l.id));
  };

  // ========================================
// COMPONENTI INPUT CON STATO LOCALE
// Per evitare perdita di focus durante la digitazione
// ========================================

// Input per numeri (ore, quantità, etc.)
const DebouncedNumberInput = ({ 
  value, 
  onUpdate, 
  step = "0.5", 
  min = "0",
  placeholder = "",
  ariaLabel = "",
  delay = 2000,
  className = "form-control table-input"
}) => {
  const [localValue, setLocalValue] = useState(value || 0);
  const timeoutRef = useRef(null);

  useEffect(() => {
    setLocalValue(value || 0);
  }, [value]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      onUpdate(newValue);
    }, delay);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <input
      type="number"
      value={localValue}
      onChange={handleChange}
      step={step}
      min={min}
      placeholder={placeholder}
      className={className}
      aria-label={ariaLabel}
    />
  );
};

// Input per testo (note, descrizioni, etc.)
const DebouncedTextInput = ({ 
  value, 
  onUpdate, 
  placeholder = "",
  ariaLabel = "",
  delay = 2000,
  className = "form-control table-input"
}) => {
  const [localValue, setLocalValue] = useState(value || '');
  const timeoutRef = useRef(null);

  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      onUpdate(newValue);
    }, delay);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <input
      type="text"
      value={localValue}
      onChange={handleChange}
      placeholder={placeholder}
      className={className}
      aria-label={ariaLabel}
    />
  );
};

  // ========================================
  // SEZIONI FORM COMPLETO
  // ========================================

  // Wrapper per la sezione personale automatica
  const PersonaleSectionWrapper = () => {
    // Controlli di sicurezza potenziati
    if (!formData || typeof formData !== 'object') {
      console.error('PersonaleSection: formData non valido:', formData);
      return (
        <div className="form-section">
          <h4>👷 Personale Presente</h4>
          <div className="alert alert-danger">
            Errore nel caricamento dati. Ricarica la pagina.
          </div>
        </div>
      );
    }

    if (!users || users.length === 0) {
      console.warn('PersonaleSection: Nessun utente caricato');
      return (
        <div className="form-section">
          <h4>👷 Personale Presente</h4>
          <div className="alert alert-warning">
            Caricamento utenti in corso...
          </div>
        </div>
      );
    }

    if (!formData.data) {
      return (
        <div className="form-section">
          <h4>👷 Personale Presente</h4>
          <div className="alert alert-info">
            ⚠️ Seleziona prima la data del rapportino per vedere le ore disponibili
          </div>
        </div>
      );
    }

    return (
      <AutoPersonaleSection 
        users={users}
        formData={formData}
        setFormData={setFormData}
        rapportinoDate={formData.data}
        editingRapportino={editingRapportino} // 🆕 NUOVO: Passa se stiamo modificando
        onRapportinoUpdate={handleRapportinoUpdate}
      />
    );
  };

 // ========================================
// COMPONENTE MEZZI SECTION - VERSIONE CORRETTA
// Con componente OreInput incluso
// ========================================

const MezziSection = () => {
  const [selectedMezzo, setSelectedMezzo] = useState('');
  const [oreMezzo, setOreMezzo] = useState('');

  // 🆕 COMPONENTE OreInput (era mancante)
  const OreInput = ({ mezzo, index, onUpdate }) => {
    const [localValue, setLocalValue] = useState(mezzo.ore || mezzo.oreLavorate || 0);
    const timeoutRef = useRef(null);

    // Sincronizza il valore locale quando il prop cambia
    useEffect(() => {
      setLocalValue(mezzo.ore || mezzo.oreLavorate || 0);
    }, [mezzo.ore, mezzo.oreLavorate]);

    const handleChange = (e) => {
      const newValue = e.target.value;
      setLocalValue(newValue); // Aggiorna immediatamente lo stato locale

      // Debounce: aggiorna lo stato globale dopo 3000ms
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      timeoutRef.current = setTimeout(() => {
        onUpdate(index, newValue);
      }, 3000);
    };

    // Cleanup del timeout
    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    return (
      <input
        type="number"
        value={localValue}
        onChange={handleChange}
        step="0.5"
        min="0"
        className="form-control table-input"
        aria-label={`Ore mezzo ${mezzo.nomeMezzo}`}
      />
    );
  };

  // Controllo di sicurezza
  if (!formData || typeof formData !== 'object') {
    console.error('MezziSection: formData non valido');
    return (
      <div className="form-section">
        <h4>🚛 Mezzi Utilizzati</h4>
        <div className="alert alert-danger">
          Errore nel caricamento dati. Ricarica la pagina.
        </div>
      </div>
    );
  }

  const addMezzo = () => {
    if (!selectedMezzo || !oreMezzo) {
      showNotification('Seleziona un mezzo e inserisci le ore di utilizzo', 'error');
      return;
    }

    const mezzo = mezzi.find(m => m.id === selectedMezzo);
    if (!mezzo) {
      showNotification('Mezzo non trovato', 'error');
      return;
    }

    const mezziUtilizzati = formData.mezziUtilizzati || [];
    const esisteGia = mezziUtilizzati.some(m => m.mezzoId === selectedMezzo);
    if (esisteGia) {
      showNotification('Questo mezzo è già stato aggiunto', 'error');
      return;
    }

    const oreNum = parseFloat(oreMezzo);
    if (oreNum <= 0) {
      showNotification('Le ore devono essere maggiori di zero', 'error');
      return;
    }

    const costoOrario = parseFloat(mezzo.costoOrario || 0);
    const totale = oreNum * costoOrario;

    const newEntry = {
      mezzoId: selectedMezzo,
      nomeMezzo: mezzo.nomeMezzo || mezzo.nome,
      targa: mezzo.targa || '',
      ore: oreNum,
      oreLavorate: oreNum,
      costoOrario: costoOrario,
      totale: totale
    };

    setFormData(prev => ({
      ...prev,
      mezziUtilizzati: [...mezziUtilizzati, newEntry]
    }));

    setSelectedMezzo('');
    setOreMezzo('');
  };

  const removeMezzo = (index) => {
    const mezziUtilizzati = formData.mezziUtilizzati || [];
    setFormData(prev => ({
      ...prev,
      mezziUtilizzati: mezziUtilizzati.filter((_, i) => i !== index)
    }));
  };

  const updateOre = (index, newOre) => {
    const mezziUtilizzati = formData.mezziUtilizzati || [];
    const updatedMezzi = [...mezziUtilizzati];
    const oreNum = parseFloat(newOre) || 0;
    const costoOrario = parseFloat(updatedMezzi[index].costoOrario || 0);

    updatedMezzi[index] = {
      ...updatedMezzi[index],
      ore: oreNum,
      oreLavorate: oreNum,
      totale: oreNum * costoOrario
    };

    setFormData(prev => ({
      ...prev,
      mezziUtilizzati: updatedMezzi
    }));
  };

  const mezziUtilizzatiPresenti = formData.mezziUtilizzati || [];
  const totaleMezzi = mezziUtilizzatiPresenti.reduce(
    (sum, m) => sum + parseFloat(m.totale || 0), 0
  );

  return (
    <div className="form-section">
      {/* 🆕 SEZIONE HEADER CON PULSANTE COPIA */}
      <div className="section-header-with-actions">
        <button
          type="button"
          onClick={handleOpenCopyMezziModal}
          className="btn btn-secondary btn-copy-mezzi"
          title="Copia mezzi e ore da un rapportino precedente"
        >
          📋 Copia da giorno precedente
        </button>
      </div>

      <div className="mezzi-form">
        <div className="form-row">
          <div className="form-group">
            <MezziAutocomplete
              mezzi={mezzi}
              selectedMezzo={selectedMezzo}
              setSelectedMezzo={setSelectedMezzo}
              onSelect={(mezzo) => setSelectedMezzo(mezzo.id)}
            />
          </div>

          <div className="form-group">
            <label>Ore di utilizzo *</label>
            <input
              type="number"
              value={oreMezzo}
              onChange={(e) => setOreMezzo(e.target.value)}
              step="0.5"
              min="0.5"
              placeholder="8.0"
              className="form-control"
            />
          </div>

          <div className="form-group">
            <button
              type="button"
              onClick={addMezzo}
              className="btn btn-primary"
              disabled={!selectedMezzo || !oreMezzo}
            >
              ➕ Aggiungi
            </button>
          </div>
        </div>
      </div>

      {/* Lista mezzi aggiunti - CON DATA-LABEL */}
      {mezziUtilizzatiPresenti.length > 0 && (
        <div className="mezzi-list">
          <table className="table">
            <thead>
              <tr>
                <th>Mezzo</th>
                <th>Targa</th>
                <th>Ore</th>
                <th>Costo/h</th>
                <th>Totale</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {mezziUtilizzatiPresenti.map((mezzo, index) => (
                <tr key={mezzo.mezzoId || `mezzo-${index}`}>
                  <td data-label="Mezzo">
                    {mezzo.nomeMezzo || getMezzoName(mezzo.mezzoId)}
                  </td>
                  <td data-label="Targa">
                    {mezzo.targa || '-'}
                  </td>
                  <td data-label="Ore">
                    <OreInput 
                      mezzo={mezzo} 
                      index={index} 
                      onUpdate={updateOre} 
                    />
                  </td>
                  <td data-label="Costo/h">
                    {formatEuro(mezzo.costoOrario)}
                  </td>
                  <td data-label="Totale">
                    <strong>{formatEuro(mezzo.totale)}</strong>
                  </td>
                  <td data-label="Azioni">
                    <button
                      type="button"
                      onClick={() => removeMezzo(index)}
                      className="btn btn-danger btn-sm"
                      title="Rimuovi mezzo"
                    >
                      🗑️ Rimuovi
                    </button>
                  </td>
                </tr>
              ))}
              <tr className="table-total">
                <td colSpan="4" data-label="">
                  <strong>TOTALE MEZZI</strong>
                </td>
                <td data-label="">
                  <strong>{formatEuro(totaleMezzi)}</strong>
                </td>
                <td data-label=""></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ========================================
// COMPONENTE MATERIALI VARIABILI - VERSIONE COMPLETA
// Sostituisci il componente MaterialiVariabiliSection (riga ~1773)
// ========================================

const MaterialiVariabiliSection = () => {
  const [selectedMateriale, setSelectedMateriale] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [costo, setCosto] = useState('');
  
  if (!formData || typeof formData !== 'object') {
    console.error('MaterialiVariabiliSection: formData non valido');
    return (
      <div className="form-section">
        <h4>📦 Materiali a Costo Variabile</h4>
        <div className="alert alert-danger">
          Errore nel caricamento dati. Ricarica la pagina.
        </div>
      </div>
    );
  }

  const addMaterialeVariabile = () => {
    if (!selectedMateriale || !costo) {
      showNotification('Seleziona un materiale e inserisci il costo', 'error');
      return;
    }

    const materiale = materialiVariabili.find(m => m.id === selectedMateriale);
    if (!materiale) {
      showNotification('Materiale non trovato', 'error');
      return;
    }

    const materialiVariabiliPresenti = formData.materialiVariabili || [];
    const esisteGia = materialiVariabiliPresenti.some(m => m.materialeId === selectedMateriale);
    if (esisteGia) {
      showNotification('Questo materiale è già stato aggiunto', 'error');
      return;
    }

    const costoNum = parseFloat(costo);
    if (costoNum <= 0) {
      showNotification('Il costo deve essere maggiore di zero', 'error');
      return;
    }

    const newEntry = {
      materialeId: selectedMateriale,
      nomeMacrovoce: materiale.nomeMacrovoce || materiale.nome,
      descrizione: descrizione.trim() || '',
      costo: costoNum
    };

    setFormData(prev => ({
      ...prev,
      materialiVariabili: [...materialiVariabiliPresenti, newEntry]
    }));

    setSelectedMateriale('');
    setDescrizione('');
    setCosto('');
  };

  const removeMaterialeVariabile = (index) => {
    const materialiVariabiliPresenti = formData.materialiVariabili || [];
    setFormData(prev => ({
      ...prev,
      materialiVariabili: materialiVariabiliPresenti.filter((_, i) => i !== index)
    }));
  };

  const updateCosto = (index, newCosto) => {
    const materialiVariabiliPresenti = formData.materialiVariabili || [];
    const updatedMateriali = [...materialiVariabiliPresenti];
    updatedMateriali[index] = {
      ...updatedMateriali[index],
      costo: parseFloat(newCosto) || 0
    };

    setFormData(prev => ({
      ...prev,
      materialiVariabili: updatedMateriali
    }));
  };

  const materialiVariabiliPresenti = formData.materialiVariabili || [];
  const totaleMaterialiVariabili = materialiVariabiliPresenti.reduce((sum, m) => sum + (m.costo || 0), 0);

  return (
    <div className="form-section">
      {/* 🆕 PULSANTE PER CREARE NUOVO MATERIALE */}
      <div className="section-header-with-actions">
        <button
          type="button"
          onClick={handleOpenNewMaterialeVariabileModal}
          className="btn btn-success btn-new-material"
          title="Crea un nuovo materiale a costo variabile"
        >
          ➕ Nuovo Materiale
        </button>
      </div>

      <div className="info-materiali-variabili">
        <small className="text-info">
          💡 <strong>La descrizione è facoltativa:</strong> Puoi aggiungere dettagli specifici solo se necessario
        </small>
      </div>

      <div className="materiali-form">
        <div className="form-row">
          <div className="form-group">
            <MaterialiVariabiliAutocomplete
              materialiVariabili={materialiVariabili}
              selectedMateriale={selectedMateriale}
              setSelectedMateriale={setSelectedMateriale}
              onSelect={(materiale) => setSelectedMateriale(materiale.id)}
            />
          </div>

          <div className="form-group">
            <label>Descrizione (facoltativa)</label>
            <input
              type="text"
              value={descrizione}
              onChange={(e) => setDescrizione(e.target.value)}
              placeholder="Dettagli aggiuntivi se necessari..."
              className="form-control"
            />
          </div>

          <div className="form-group">
            <label>Costo (€) *</label>
            <input
              type="number"
              value={costo}
              onChange={(e) => setCosto(e.target.value)}
              step="0.01"
              min="0.01"
              placeholder="150.00"
              className="form-control"
            />
          </div>

          <div className="form-group">
            <button
              type="button"
              onClick={addMaterialeVariabile}
              className="btn btn-primary"
              disabled={!selectedMateriale || !costo}
            >
              ➕ Aggiungi
            </button>
          </div>
        </div>
      </div>

      {/* Lista materiali variabili aggiunti */}
      {materialiVariabiliPresenti.length > 0 && (
        <div className="materiali-variabili-list">
          <table className="table">
            <thead>
              <tr>
                <th>Materiale</th>
                <th>Descrizione</th>
                <th>Costo</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {materialiVariabiliPresenti.map((materiale, index) => (
                <tr key={materiale.materialeId || `mat-var-${index}`}>
                  <td data-label="Materiale">
                    {materiale.nomeMacrovoce || getMaterialeVariabileName(materiale.materialeId)}
                  </td>
                  <td data-label="Descrizione">
                    <DebouncedTextInput
                      value={materiale.descrizione || ''}
                      onUpdate={(newValue) => {
                        const materialiVariabiliPresenti = formData.materialiVariabili || [];
                        const updatedMateriali = [...materialiVariabiliPresenti];
                        updatedMateriali[index] = {
                          ...updatedMateriali[index],
                          descrizione: newValue
                        };
                        setFormData(prev => ({
                          ...prev,
                          materialiVariabili: updatedMateriali
                        }));
                      }}
                      placeholder="Aggiungi descrizione..."
                      ariaLabel={`Descrizione ${materiale.nomeMacrovoce}`}
                    />
                  </td>
                  <td data-label="Costo">
                    <DebouncedNumberInput
                      value={materiale.costo || 0}
                      onUpdate={(newValue) => updateCosto(index, newValue)}
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      ariaLabel={`Costo ${materiale.nomeMacrovoce}`}
                    />
                  </td>
                  <td data-label="Azioni">
                    <button
                      type="button"
                      onClick={() => removeMaterialeVariabile(index)}
                      className="btn btn-danger btn-sm"
                      title="Rimuovi materiale"
                    >
                      🗑️ Rimuovi
                    </button>
                  </td>
                </tr>
              ))}
              <tr className="table-total">
                <td colSpan="2" data-label="">
                  <strong>TOTALE MATERIALI VARIABILI</strong>
                </td>
                <td data-label="">
                  <strong>{formatEuro(totaleMaterialiVariabili)}</strong>
                </td>
                <td data-label=""></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ========================================
// COMPONENTE MATERIALI FISSI - VERSIONE COMPLETA
// Sostituisci il componente MaterialiFissiSection (riga ~1976)
// ========================================

const MaterialiFissiSection = () => {
  const [selectedMateriale, setSelectedMateriale] = useState('');
  const [quantita, setQuantita] = useState('');

  if (!formData || typeof formData !== 'object') {
    console.error('MaterialiFissiSection: formData non valido');
    return (
      <div className="form-section">
        <h4>🏗️ Materiali a Costo Fisso</h4>
        <div className="alert alert-danger">
          Errore nel caricamento dati. Ricarica la pagina.
        </div>
      </div>
    );
  }

  const addMaterialeFisso = () => {
    if (!selectedMateriale || !quantita) {
      showNotification('Seleziona un materiale e inserisci la quantità', 'error');
      return;
    }

    const materiale = materialiFissi.find(m => m.id === selectedMateriale);
    if (!materiale) {
      showNotification('Materiale non trovato', 'error');
      return;
    }

    const materialiFissiPresenti = formData.materialiFissi || [];
    const esisteGia = materialiFissiPresenti.some(m => m.materialeId === selectedMateriale);
    if (esisteGia) {
      showNotification('Questo materiale è già stato aggiunto', 'error');
      return;
    }

    const quantitaNum = parseFloat(quantita);
    if (quantitaNum <= 0) {
      showNotification('La quantità deve essere maggiore di zero', 'error');
      return;
    }

    const prezzoBase = parseFloat(materiale.prezzoUnitario || 0);
    const costoTrasporto = parseFloat(cantiere.variazioneCostiMateriali || 0);
    const prezzoTotale = prezzoBase + costoTrasporto;
    const totale = quantitaNum * prezzoTotale;

    const newEntry = {
      materialeId: selectedMateriale,
      nomeMateriale: materiale.nomeMateriale || materiale.nome,
      unitaMisura: materiale.unitaMisura || 'pz',
      quantita: quantitaNum,
      prezzoUnitarioBase: prezzoBase,
      costoTrasporto: costoTrasporto,
      prezzoUnitario: prezzoTotale,
      totale: totale
    };

    setFormData(prev => ({
      ...prev,
      materialiFissi: [...prev.materialiFissi || [], newEntry]
    }));

    setSelectedMateriale('');
    setQuantita('');
  };

  const removeMaterialeFisso = (index) => {
    setFormData(prev => ({
      ...prev,
      materialiFissi: (prev.materialiFissi || []).filter((_, i) => i !== index)
    }));
  };

  const updateQuantita = (index, newQuantita) => {
    const materialiFissiPresenti = formData.materialiFissi || [];
    const updatedMateriali = [...materialiFissiPresenti];
    const quantitaNum = parseFloat(newQuantita) || 0;
    const prezzoUnitario = parseFloat(updatedMateriali[index].prezzoUnitario || 0);

    updatedMateriali[index] = {
      ...updatedMateriali[index],
      quantita: quantitaNum,
      totale: quantitaNum * prezzoUnitario
    };

    setFormData(prev => ({
      ...prev,
      materialiFissi: updatedMateriali
    }));
  };

  const materialiFissiPresenti = formData.materialiFissi || [];
  const totaleMaterialiFissi = materialiFissiPresenti.reduce((sum, m) => sum + parseFloat(m.totale || 0), 0);

  return (
    <div className="form-section">
      {/* 🆕 PULSANTE PER CREARE NUOVO MATERIALE */}
      <div className="section-header-with-actions">
        <button
          type="button"
          onClick={handleOpenNewMaterialeFissoModal}
          className="btn btn-success btn-new-material"
          title="Crea un nuovo materiale a costo fisso"
        >
          ➕ Nuovo Materiale
        </button>
      </div>

      <div className="info-trasporto">
        <small className="text-info">
          🚛 <strong>Costo trasporto cantiere:</strong> {formatEuro(parseFloat(cantiere.variazioneCostiMateriali || 0))}/ton
          (viene aggiunto automaticamente al prezzo base)
        </small>
      </div>

      <div className="materiali-fissi-form">
        <div className="form-row">
          <div className="form-group">
            <MaterialiFissiAutocomplete
              materialiFissi={materialiFissi}
              selectedMateriale={selectedMateriale}
              setSelectedMateriale={setSelectedMateriale}
              onSelect={(materiale) => setSelectedMateriale(materiale.id)}
            />
          </div>

          <div className="form-group">
            <label>Quantità *</label>
            <input
              type="number"
              value={quantita}
              onChange={(e) => setQuantita(e.target.value)}
              step="0.01"
              min="0.01"
              placeholder="10.5"
              className="form-control"
            />
          </div>

          <div className="form-group">
            <button
              type="button"
              onClick={addMaterialeFisso}
              className="btn btn-primary"
              disabled={!selectedMateriale || !quantita}
            >
              ➕ Aggiungi
            </button>
          </div>
        </div>
      </div>

      {materialiFissiPresenti.length > 0 && (
        <div className="materiali-fissi-list">
          <table className="table">
            <thead>
              <tr>
                <th>Materiale</th>
                <th>U.M.</th>
                <th>Quantità</th>
                <th>Prezzo Base</th>
                <th>Trasporto</th>
                <th>Prezzo Tot.</th>
                <th>Totale</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {materialiFissiPresenti.map((materiale, index) => (
                <tr key={materiale.materialeId || `materiale-fisso-${index}`}>
                  <td data-label="Materiale">
                    {materiale.nomeMateriale || getMaterialeFissoName(materiale.materialeId)}
                  </td>
                  <td data-label="U.M.">
                    {materiale.unitaMisura || 'pz'}
                  </td>
                  <td data-label="Quantità">
                    <DebouncedNumberInput
                      value={materiale.quantita || 0}
                      onUpdate={(newValue) => updateQuantita(index, newValue)}
                      step="0.01"
                      min="0"
                      ariaLabel={`Quantità ${materiale.nomeMateriale}`}
                    />
                  </td>
                  <td data-label="Prezzo Base">
                    {formatEuro(materiale.prezzoUnitarioBase)}
                  </td>
                  <td data-label="Trasporto">
                    <span className="trasporto-badge">
                      +{formatEuro(materiale.costoTrasporto)}
                    </span>
                  </td>
                  <td data-label="Prezzo Tot.">
                    <strong>{formatEuro(materiale.prezzoUnitario)}</strong>
                  </td>
                  <td data-label="Totale">
                    <strong>{formatEuro(materiale.totale)}</strong>
                  </td>
                  <td data-label="Azioni">
                    <button
                      type="button"
                      onClick={() => removeMaterialeFisso(index)}
                      className="btn btn-danger btn-sm"
                      title="Rimuovi materiale"
                    >
                      🗑️ Rimuovi
                    </button>
                  </td>
                </tr>
              ))}
              <tr className="table-total">
                <td colSpan="6" data-label="">
                  <strong>TOTALE MATERIALI FISSI</strong>
                </td>
                <td data-label="">
                  <strong>{formatEuro(totaleMaterialiFissi)}</strong>
                </td>
                <td data-label=""></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};


  // Sezione Lavorazioni Completa
  const LavorazioniSection = () => {
    const [selectedLavorazione, setSelectedLavorazione] = useState('');
    const [note, setNote] = useState('');
    const [isNewLavorazione, setIsNewLavorazione] = useState(false);
    const [newLavorazioneName, setNewLavorazioneName] = useState('');

    // Controllo di sicurezza
    if (!formData || typeof formData !== 'object') {
      console.error('LavorazioniSection: formData non valido');
      return (
        <div className="form-section">
          <h4>⚒️ Lavorazioni Eseguite</h4>
          <div className="alert alert-danger">
            Errore nel caricamento dati. Ricarica la pagina.
          </div>
        </div>
      );
    }

    const addLavorazione = () => {
      let lavorazioneName = '';
      let lavorazioneId = '';
      let isCustom = false;

      if (isNewLavorazione) {
        if (!newLavorazioneName.trim()) {
          showNotification('Inserisci il nome della nuova lavorazione', 'error');
          return;
        }
        lavorazioneName = newLavorazioneName.trim();
        lavorazioneId = `custom_${Date.now()}`;
        isCustom = true;
      } else {
        if (!selectedLavorazione) {
          showNotification('Seleziona una lavorazione', 'error');
          return;
        }
        const lavorazione = lavorazioni.find(l => l.id === selectedLavorazione);
        if (!lavorazione) {
          showNotification('Lavorazione non trovata', 'error');
          return;
        }
        lavorazioneName = lavorazione.nomeLavorazione || lavorazione.nome;
        lavorazioneId = selectedLavorazione;
        isCustom = false;
      }

      // Verifica se la lavorazione è già presente
      const lavorazioniPresenti = formData.lavorazioni || [];
      const esisteGia = lavorazioniPresenti.some(l => 
        l.lavorazioneId === lavorazioneId || 
        (isCustom && l.nomeLavorazione === lavorazioneName)
      );
      if (esisteGia) {
        showNotification('Questa lavorazione è già stata aggiunta', 'error');
        return;
      }

      const newEntry = {
        lavorazioneId: lavorazioneId,
        nomeLavorazione: lavorazioneName,
        descrizione: '',
        note: note.trim() || '',
        isCustom: isCustom
      };

      setFormData(prev => ({
        ...prev,
        lavorazioni: [...lavorazioniPresenti, newEntry]
      }));

      // Reset form
      setSelectedLavorazione('');
      setNote('');
      setNewLavorazioneName('');
      setIsNewLavorazione(false);
    };

    const removeLavorazione = (index) => {
      const lavorazioniPresenti = formData.lavorazioni || [];
      setFormData(prev => ({
        ...prev,
        lavorazioni: lavorazioniPresenti.filter((_, i) => i !== index)
      }));
    };

    const updateNote = (index, newNote) => {
      const lavorazioniPresenti = formData.lavorazioni || [];
      const updatedLavorazioni = [...lavorazioniPresenti];
      updatedLavorazioni[index] = {
        ...updatedLavorazioni[index],
        note: newNote
      };
      
      setFormData(prev => ({
        ...prev,
        lavorazioni: updatedLavorazioni
      }));
    };

    const lavorazioniPresenti = formData.lavorazioni || [];

    return (
      <div className="form-section">
        
        <div className="lavorazioni-form">
          <div className="form-row">
            {/* Toggle per nuova lavorazione */}
            <div className="form-group full-width">
              <div className="toggle-section">
                <label>
                  <input
                    type="checkbox"
                    checked={isNewLavorazione}
                    onChange={(e) => {
                      setIsNewLavorazione(e.target.checked);
                      setSelectedLavorazione('');
                      setNewLavorazioneName('');
                    }}
                  />
                  Aggiungi nuova lavorazione personalizzata
                </label>
              </div>
            </div>

            {/* Selezione lavorazione esistente o nuova */}
            {!isNewLavorazione ? (
              <div className="form-group">
              <LavorazioniAutocomplete
                                lavorazioni={lavorazioni}
                                selectedLavorazione={selectedLavorazione}
                                setSelectedLavorazione={setSelectedLavorazione}
                                onSelect={(lavorazione) => setSelectedLavorazione(lavorazione.id)}
                              />
                            </div>
                          ) : (
                            <div className="form-group">
                              <label>Nome nuova lavorazione *</label>
                              <input
                                type="text"
                                value={newLavorazioneName}
                                onChange={(e) => setNewLavorazioneName(e.target.value)}
                                placeholder="Es: Posa pavimentazione..."
                                className="form-control"
                              />
                            </div>
                          )}
                          
                          <div className="form-group">
                            <label>Note aggiuntive</label>
                            <input
                              type="text"
                              value={note}
                              onChange={(e) => setNote(e.target.value)}
                              placeholder="Note specifiche sulla lavorazione..."
                              className="form-control"
                            />
                          </div>
                          
                          <div className="form-group">
                            <button 
                              type="button" 
                              onClick={addLavorazione}
                              className="btn btn-primary"
                              disabled={(!selectedLavorazione && !isNewLavorazione) || (isNewLavorazione && !newLavorazioneName.trim())}
                            >
                              ➕ Aggiungi
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Lista lavorazioni aggiunte */}
                      {lavorazioniPresenti.length > 0 && (
                        <div className="lavorazioni-list">
                          <table className="table">
                            <thead>
                              <tr>
                                <th>Lavorazione</th>
                                <th>Note</th>
                                <th>Tipo</th>
                                <th>Azioni</th>
                              </tr>
                            </thead>
                            <tbody>
                              {lavorazioniPresenti.map((lavorazione, index) => (
                                <tr key={index}>
                                  <td>{lavorazione.nomeLavorazione || getLavorazioneName(lavorazione.lavorazioneId)}</td>
                                  <td>
                                    <input
                                      type="text"
                                      value={lavorazione.note || ''}
                                      onChange={(e) => updateNote(index, e.target.value)}
                                      placeholder="Aggiungi note..."
                                      className="form-control table-input"
                                    />
                                  </td>
                                  <td>
                                    {lavorazione.isCustom ? (
                                      <span className="badge badge-success">Personalizzata</span>
                                    ) : (
                                      <span className="badge badge-primary">Standard</span>
                                    )}
                                  </td>
                                  <td>
                                    <button 
                                      type="button"
                                      onClick={() => removeLavorazione(index)}
                                      className="btn btn-danger btn-sm"
                                      title="Rimuovi lavorazione"
                                    >
                                      🗑️
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                };
              
                // Sezione Ricavi Completa
const RicaviSection = () => {
  const [descrizioneRicavo, setDescrizioneRicavo] = useState('');
  const [importo, setImporto] = useState('');

  // Controllo di sicurezza
  if (!formData || typeof formData !== 'object') {
    console.error('RicaviSection: formData non valido');
    return (
      <div className="form-section">
        <h4>💰 Ricavi</h4>
        <div className="alert alert-danger">
          Errore nel caricamento dati. Ricarica la pagina.
        </div>
      </div>
    );
  }

  const addRicavo = () => {
    if (!descrizioneRicavo.trim() || !importo) {
      showNotification('Inserisci descrizione e importo del ricavo', 'error');
      return;
    }

    const importoNum = parseFloat(importo);
    if (importoNum <= 0) {
      showNotification('L\'importo deve essere maggiore di zero', 'error');
      return;
    }

    const ricaviPresenti = formData.ricavi || [];
    const newEntry = {
      descrizione: descrizioneRicavo.trim(),
      importo: importoNum
    };

    setFormData(prev => ({
      ...prev,
      ricavi: [...ricaviPresenti, newEntry]
    }));

    setDescrizioneRicavo('');
    setImporto('');
  };

  const removeRicavo = (index) => {
    const ricaviPresenti = formData.ricavi || [];
    setFormData(prev => ({
      ...prev,
      ricavi: ricaviPresenti.filter((_, i) => i !== index)
    }));
  };

  const updateImporto = (index, newImporto) => {
    const ricaviPresenti = formData.ricavi || [];
    const updatedRicavi = [...ricaviPresenti];
    updatedRicavi[index] = {
      ...updatedRicavi[index],
      importo: parseFloat(newImporto) || 0
    };

    setFormData(prev => ({
      ...prev,
      ricavi: updatedRicavi
    }));
  };

  const ricaviPresenti = formData.ricavi || [];
  const totaleRicavi = ricaviPresenti.reduce((sum, r) => sum + (r.importo || 0), 0);

  return (
    <div className="form-section">
      <div className="ricavi-form">
        <div className="form-row">
          <div className="form-group">
            <label>Descrizione ricavo *</label>
            <input
              type="text"
              value={descrizioneRicavo}
              onChange={(e) => setDescrizioneRicavo(e.target.value)}
              placeholder="Descrizione del ricavo..."
              className="form-control"
            />
          </div>

          <div className="form-group">
            <label>Importo (€) *</label>
            <input
              type="number"
              value={importo}
              onChange={(e) => setImporto(e.target.value)}
              step="0.01"
              min="0.01"
              placeholder="1000.00"
              className="form-control"
            />
          </div>

          <div className="form-group">
            <button
              type="button"
              onClick={addRicavo}
              className="btn btn-primary"
              disabled={!descrizioneRicavo.trim() || !importo}
            >
              ➕ Aggiungi
            </button>
          </div>
        </div>
      </div>

      {/* Lista ricavi aggiunti */}
      {ricaviPresenti.length > 0 && (
        <div className="ricavi-list">
          <table className="table">
            <thead>
              <tr>
                <th>Descrizione</th>
                <th>Importo</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {ricaviPresenti.map((ricavo, index) => (
                <tr key={index}>
                  <td>{ricavo.descrizione}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="number"
                        value={ricavo.importo || 0}
                        onChange={(e) => updateImporto(index, e.target.value)}
                        step="0.01"
                        min="0"
                        className="form-control table-input"
                      />
                      <span>{formatEuro(ricavo.importo)}</span>
                    </div>
                  </td>
                  <td>
                    <button
                      type="button"
                      onClick={() => removeRicavo(index)}
                      className="btn btn-danger btn-sm"
                      title="Rimuovi ricavo"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
              <tr className="table-total">
                <td><strong>TOTALE RICAVI</strong></td>
                <td><strong>{formatEuro(totaleRicavi)}</strong></td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Sezione Spese Varie Completa
const SpeseVarieSection = () => {
  const [descrizione, setDescrizione] = useState('');
  const [importo, setImporto] = useState('');
  const [categoria, setCategoria] = useState('Spese Varie');

  // Controllo di sicurezza
  if (!formData || typeof formData !== 'object') {
    console.error('SpeseVarieSection: formData non valido');
    return (
      <div className="form-section">
        <h4>💳 Spese Varie</h4>
        <div className="alert alert-danger">
          Errore nel caricamento dati. Ricarica la pagina.
        </div>
      </div>
    );
  }

  const addSpesa = () => {
    if (!descrizione.trim()) {
      showNotification('Inserisci una descrizione per la spesa', 'error');
      return;
    }

    if (!importo || parseFloat(importo) <= 0) {
      showNotification('Inserisci un importo valido maggiore di zero', 'error');
      return;
    }

    const spesePresenti = formData.speseVarie || [];
    const newEntry = {
      descrizione: descrizione.trim(),
      importo: parseFloat(importo),
      categoria: categoria
    };

    setFormData(prev => ({
      ...prev,
      speseVarie: [...spesePresenti, newEntry]
    }));

    // Reset campi
    setDescrizione('');
    setImporto('');
    setCategoria('Spese Varie');
  };

  const removeSpesa = (index) => {
    const spesePresenti = formData.speseVarie || [];
    setFormData(prev => ({
      ...prev,
      speseVarie: spesePresenti.filter((_, i) => i !== index)
    }));
  };

  const updateSpesa = (index, field, value) => {
    const spesePresenti = formData.speseVarie || [];
    const updatedSpese = [...spesePresenti];
    
    if (field === 'importo') {
      updatedSpese[index] = {
        ...updatedSpese[index],
        importo: parseFloat(value) || 0
      };
    } else {
      updatedSpese[index] = {
        ...updatedSpese[index],
        [field]: value
      };
    }

    setFormData(prev => ({
      ...prev,
      speseVarie: updatedSpese
    }));
  };

  const spesePresenti = formData.speseVarie || [];
  
  // Calcola i totali per categoria
  const totaliPerCategoria = spesePresenti.reduce((acc, spesa) => {
    const cat = spesa.categoria || 'Spese Varie';
    acc[cat] = (acc[cat] || 0) + parseFloat(spesa.importo || 0);
    return acc;
  }, {});

  const totaleSpese = spesePresenti.reduce((sum, s) => sum + parseFloat(s.importo || 0), 0);

  const getCategoriaIcon = (cat) => {
    switch(cat) {
      case 'Personale': return '👷';
      case 'Mezzi': return '🚛';
      case 'Materiali Variabili': return '📦';
      case 'Materiali Fissi': return '🏗️';
      case 'Spese Varie': return '💳';
      default: return '💳';
    }
  };

  const getCategoriaColor = (cat) => {
    switch(cat) {
      case 'Personale': return '#3498db';
      case 'Mezzi': return '#e74c3c';
      case 'Materiali Variabili': return '#2ecc71';
      case 'Materiali Fissi': return '#27ae60';
      case 'Spese Varie': return '#9b59b6';
      default: return '#9b59b6';
    }
  };

  return (
    <div className="form-section">
      {/* Info box */}
      <div className="info-box" style={{ 
        background: '#f8f9fa', 
        padding: '12px', 
        borderRadius: '6px', 
        marginBottom: '15px',
        border: '1px solid #dee2e6'
      }}>
        <p style={{ margin: 0, fontSize: '0.9em', color: '#495057' }}>
          💡 <strong>Info:</strong> Le spese inserite qui verranno automaticamente aggiunte alla categoria selezionata nel riepilogo economico del rapportino.
        </p>
      </div>

      {/* Form per aggiungere nuova spesa */}
      <div className="spese-varie-form" style={{ marginBottom: '20px' }}>
        <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.5fr 0.5fr', gap: '10px', alignItems: 'end' }}>
          <div className="form-group">
            <label>Descrizione spesa *</label>
            <input
              type="text"
              value={descrizione}
              onChange={(e) => setDescrizione(e.target.value)}
              placeholder="Es: Costo Personale fino al 31/12/2025"
              className="form-control"
            />
          </div>

          <div className="form-group">
            <label>Importo (€) *</label>
            <input
              type="number"
              value={importo}
              onChange={(e) => setImporto(e.target.value)}
              step="0.01"
              min="0.01"
              placeholder="250000.00"
              className="form-control"
            />
          </div>

          <div className="form-group">
            <label>Categoria report *</label>
            <select
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
              className="form-control"
            >
              <option value="Personale">👷 Personale</option>
              <option value="Mezzi">🚛 Mezzi</option>
              <option value="Materiali Variabili">📦 Materiali Variabili</option>
              <option value="Materiali Fissi">🏗️ Materiali Fissi</option>
              <option value="Spese Varie">💳 Spese Varie</option>
            </select>
          </div>

          <div className="form-group">
            <button
              type="button"
              onClick={addSpesa}
              className="btn btn-primary"
              disabled={!descrizione.trim() || !importo}
              style={{ width: '100%' }}
            >
              ➕
            </button>
          </div>
        </div>
      </div>

      {/* Lista spese aggiunte */}
      {spesePresenti.length > 0 && (
        <>
          <div className="spese-varie-list">
            <table className="table">
              <thead>
                <tr>
                  <th>Descrizione</th>
                  <th>Importo</th>
                  <th>Categoria Report</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {spesePresenti.map((spesa, index) => (
                  <tr key={index}>
                    <td data-label="Descrizione">
                      <input
                        type="text"
                        value={spesa.descrizione || ''}
                        onChange={(e) => updateSpesa(index, 'descrizione', e.target.value)}
                        className="form-control table-input"
                        placeholder="Descrizione spesa"
                      />
                    </td>
                    <td data-label="Importo">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="number"
                          value={spesa.importo || 0}
                          onChange={(e) => updateSpesa(index, 'importo', e.target.value)}
                          step="0.01"
                          min="0"
                          className="form-control table-input"
                          style={{ maxWidth: '120px' }}
                        />
                        <strong style={{ minWidth: '80px' }}>{formatEuro(spesa.importo || 0)}</strong>
                      </div>
                    </td>
                    <td data-label="Categoria">
                      <select
                        value={spesa.categoria || 'Spese Varie'}
                        onChange={(e) => updateSpesa(index, 'categoria', e.target.value)}
                        className="form-control table-input"
                        style={{ 
                          borderLeft: `3px solid ${getCategoriaColor(spesa.categoria)}`,
                          fontWeight: '500'
                        }}
                      >
                        <option value="Personale">👷 Personale</option>
                        <option value="Mezzi">🚛 Mezzi</option>
                        <option value="Materiale">📦 Materiale</option>
                        <option value="Spese Varie">💳 Spese Varie</option>
                      </select>
                    </td>
                    <td data-label="Azioni">
                      <button
                        type="button"
                        onClick={() => removeSpesa(index)}
                        className="btn btn-danger btn-sm"
                        title="Rimuovi spesa"
                      >
                        🗑️ Rimuovi
                      </button>
                    </td>
                  </tr>
                ))}
                
                {/* Riga riepilogo per categoria */}
                <tr className="table-summary" style={{ background: '#f8f9fa', fontWeight: '500' }}>
                  <td colSpan="4">
                    <div style={{ padding: '10px 0' }}>
                      <strong>Riepilogo per categoria:</strong>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', marginTop: '8px' }}>
                        {Object.entries(totaliPerCategoria).map(([cat, totale]) => (
                          <div key={cat} style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            padding: '8px 12px',
                            background: 'white',
                            borderRadius: '4px',
                            borderLeft: `3px solid ${getCategoriaColor(cat)}`
                          }}>
                            <span>{getCategoriaIcon(cat)} {cat}:</span>
                            <span>{formatEuro(totale)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </td>
                </tr>

                {/* Totale generale */}
                <tr className="table-total">
                  <td colSpan="2" data-label="">
                    <strong>TOTALE SPESE VARIE</strong>
                  </td>
                  <td colSpan="2" data-label="">
                    <strong style={{ fontSize: '1.1em', color: '#9b59b6' }}>{formatEuro(totaleSpese)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}

      {spesePresenti.length === 0 && (
        <div className="empty-state" style={{ 
          textAlign: 'center', 
          padding: '30px', 
          background: '#f8f9fa',
          borderRadius: '6px',
          color: '#6c757d'
        }}>
          <p style={{ margin: 0, fontSize: '0.95em' }}>
            Nessuna spesa inserita. Compila i campi sopra per aggiungere una nuova spesa.
          </p>
        </div>
      )}
    </div>
  );
};
              
// Sezione Allegati Completa e Sicura
const AllegatiSection = () => {
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    console.log('🚀 Inizio processo upload:', files.length, 'file');
    setUploadingFiles(true);
    setUploadProgress(files.map(f => ({ name: f.name, progress: 0, status: 'waiting' })));
    
    try {
      // ✅ FASE 1: VALIDAZIONE PRELIMINARE
      console.log('🔍 Fase 1: Validazione file...');
      for (const file of files) {
        console.log(`📄 Validazione: ${file.name}`, {
          type: file.type,
          size: `${(file.size / 1024).toFixed(1)}KB`
        });
        
        // Validazione tipo
        const allowedTypes = [
          'image/jpeg', 'image/jpg', 'image/png', 
          'image/gif', 'image/webp', 'pdf'
        ];
        if (!allowedTypes.includes(file.type)) {
          throw new Error(
            `❌ ${file.name}: Tipo non supportato (${file.type})\n` +
            `✅ Accettati: JPG, PNG, GIF, WebP, PDF`
          );
        }
        
        // Validazione dimensione
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
          throw new Error(
            `❌ ${file.name}: File troppo grande\n` +
            `📏 Dimensione: ${(file.size / 1024 / 1024).toFixed(2)}MB\n` +
            `✅ Massimo: 10MB`
          );
        }
      }
      console.log('✅ Validazione completata');

      // ✅ FASE 2: UPLOAD SEQUENZIALE
      console.log('📤 Fase 2: Upload file...');
      const uploadedFiles = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Aggiorna progress
        setUploadProgress(prev => prev.map((p, idx) => 
          idx === i ? { ...p, status: 'uploading', progress: 10 } : p
        ));
        
        try {
          console.log(`⏳ Upload ${i + 1}/${files.length}: ${file.name}`);
          
          // Genera nome file unico
          const timestamp = Date.now();
          const randomId = Math.random().toString(36).substring(2, 8);
          const fileExtension = file.name.split('.').pop().toLowerCase();
          const baseName = formData.data?.replace(/[^a-zA-Z0-9]/g, '_') || 'rapportino';
          const finalFileName = `${baseName}_${timestamp}_${randomId}.${fileExtension}`;
          
          console.log(`📝 Nome generato: ${finalFileName}`);
          
          // Simula progress
          setUploadProgress(prev => prev.map((p, idx) => 
            idx === i ? { ...p, progress: 50 } : p
          ));
          
          // Upload effettivo
          const downloadURL = await uploadRapportinoFile(file, cantiere.id, finalFileName);
          
          if (!downloadURL) {
            throw new Error('❌ Upload fallito: URL non ricevuto');
          }
          
          console.log(`✅ Upload ${i + 1} completato:`, downloadURL.substring(0, 50) + '...');
          
          // Aggiorna progress
          setUploadProgress(prev => prev.map((p, idx) => 
            idx === i ? { ...p, status: 'completed', progress: 100 } : p
          ));
          
          // Crea oggetto allegato
          const allegatoObj = {
            nome: file.name,
            nomeFile: finalFileName,
            url: downloadURL,
            tipo: file.type,
            dimensione: file.size,
            uploadedAt: new Date().toISOString(),
            uploadedBy: currentUser?.uid || 'unknown'
          };
          
          uploadedFiles.push(allegatoObj);
          
          // Notifica progresso
          showNotification(
            `✅ ${i + 1}/${files.length} completato: ${file.name}`,
            'info'
          );
          
        } catch (fileError) {
          console.error(`❌ Errore upload ${file.name}:`, fileError);
          
          // Aggiorna progress con errore
          setUploadProgress(prev => prev.map((p, idx) => 
            idx === i ? { ...p, status: 'error', progress: 0 } : p
          ));
          
          throw new Error(`❌ Errore su "${file.name}": ${fileError.message}`);
        }
      }

      // ✅ FASE 3: AGGIORNAMENTO FORM
      console.log('💾 Fase 3: Aggiornamento form data...');
      const allegatiAttuali = formData.allegati || [];
      const nuoviAllegati = [...allegatiAttuali, ...uploadedFiles];
      
      setFormData(prev => ({
        ...prev,
        allegati: nuoviAllegati
      }));

      console.log('🎉 Processo completato con successo!');
      showNotification(
        `🎉 ${uploadedFiles.length} file caricati con successo!`,
        'success'
      );
      
    } catch (error) {
      console.error('💥 Errore nel processo upload:', error);
      showNotification(
        `💥 Errore upload: ${error.message}`,
        'error'
      );
    } finally {
      setUploadingFiles(false);
      setUploadProgress([]);
      e.target.value = ''; // Reset input
    }
  };

  const removeAllegato = (index) => {
    const allegatiAttuali = formData.allegati || [];
    const allegatoRimosso = allegatiAttuali[index];
    
    console.log('🗑️ Rimozione allegato:', allegatoRimosso?.nome);
    
    setFormData(prev => ({
      ...prev,
      allegati: allegatiAttuali.filter((_, i) => i !== index)
    }));
    
    showNotification('🗑️ Allegato rimosso', 'info');
  };

  const allegatiPresenti = formData.allegati || [];

  return (
    <div className="form-section">
      
      {/* SEZIONE UPLOAD */}
      <div className="upload-section">
        <div className="upload-input-container">
          <input
            type="file"
            onChange={handleFileUpload}
            multiple
            accept="image/*,.pdf"
            disabled={uploadingFiles}
            className="form-control file-input"
            id="file-upload-rapportino"
          />
          <label htmlFor="file-upload-rapportino" className="upload-label">
            {uploadingFiles ? (
              <div className="uploading-indicator">
                <div className="upload-spinner"></div>
                <span>📤 Upload in corso...</span>
              </div>
            ) : (
              <div className="upload-prompt">
                <span className="upload-icon">📁</span>
                <span className="upload-text">Clicca per selezionare file</span>
                <small className="upload-hint">
                  📷 Immagini (JPG, PNG, GIF, WebP) e 📄 PDF<br/>
                  📏 Massimo 10MB per file
                </small>
              </div>
            )}
          </label>
        </div>
        
        {/* PROGRESS UPLOAD */}
        {uploadProgress.length > 0 && (
          <div className="upload-progress-container">
            <h5>📊 Progresso Upload:</h5>
            {uploadProgress.map((progress, index) => (
              <div key={index} className="progress-item">
                <div className="progress-info">
                  <span className="progress-name">{progress.name}</span>
                  <span className={`progress-status status-${progress.status}`}>
                    {progress.status === 'waiting' && '⏳ In attesa...'}
                    {progress.status === 'uploading' && '📤 Caricamento...'}
                    {progress.status === 'completed' && '✅ Completato'}
                    {progress.status === 'error' && '❌ Errore'}
                  </span>
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ width: `${progress.progress}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* LISTA ALLEGATI */}
      {allegatiPresenti.length > 0 && (
        <div className="allegati-list">
          <h5>📎 File allegati ({allegatiPresenti.length}):</h5>
          <div className="allegati-grid">
            {allegatiPresenti.map((allegato, index) => (
              <div key={index} className="allegato-card">
                <div className="allegato-header">
                  <div className="allegato-icon">
                    {allegato.tipo?.startsWith('image/') ? '🖼️' : '📄'}
                  </div>
                  <div className="allegato-info">
                    <div className="allegato-nome" title={allegato.nome}>
                      {allegato.nome}
                    </div>
                    <div className="allegato-meta">
                      <span>{(allegato.dimensione / 1024).toFixed(1)} KB</span>
                      {allegato.uploadedAt && (
                        <span>
                          {new Date(allegato.uploadedAt).toLocaleDateString('it-IT')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="allegato-actions">
                  <a 
                    href={allegato.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-secondary"
                  >
                    👁️ Vedi
                  </a>
                  <button 
                    type="button"
                    onClick={() => removeAllegato(index)}
                    className="btn btn-danger btn-sm"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {allegatiPresenti.length === 0 && (
        <div className="no-allegati">
          <p>📭 Nessun file allegato</p>
        </div>
      )}
    </div>
  );
};
              
                // Sezioni del form rapido
                const sections = [
                  { id: 'informazioni-base', label: 'Informazioni Base', icon: '📋' },
                  { id: 'personale', label: 'Selezione Personale', icon: '👥' },
                  { id: 'mezzi', label: 'Mezzi Utilizzati', icon: '🚛' },
                  { id: 'materiali-variabili', label: 'Materiali Variabili', icon: '📦' },
                  { id: 'materiali-fissi', label: 'Materiali Fissi', icon: '🔧' },
                  { id: 'lavorazioni', label: 'Lavorazioni Eseguite', icon: '⚡' },
                  { id: 'ricavi', label: 'Ricavi', icon: '💰' },
                  { id: 'spese-varie', label: 'Spese Varie', icon: '💳' } // 🆕 NUOVA TAB
                ];

// 🆕 NUOVO: Componente per mostrare lo stato distribuzione
const DistributionStatusIndicator = ({ rapportino }) => {
  if (!rapportino) return null;
  
  const hasDistributedHours = rapportino.personalePresente?.some(p => p.distributedHours === true);
  const distributionMetadata = rapportino.distributionMetadata;
  
  if (!hasDistributedHours && !distributionMetadata) return null;
};
              
                // Render delle sezioni del form rapido
                const renderQuickFormSection = () => {
                  if (!selectedRapportino) {
                    return (
                      <div className="quick-form-empty">
                        <h3>Nessun rapportino selezionato</h3>
                        <p>Seleziona un rapportino dalla lista per visualizzarne i dettagli</p>
                      </div>
                    );
                  }
              
                  switch (selectedSection) {
                    case 'informazioni-base':
                      return (
                        <div className="quick-form-section">
                          <h3>📋 Informazioni Base</h3>
                          <div className="form-grid">
                            <div className="form-group">
                              <label>Data:</label>
                              <input 
                                type="date" 
                                value={formData.data || ''} 
                                onChange={(e) => setFormData(prev => ({...prev, data: e.target.value}))}
                                className="form-control"
                              />
                            </div>
                            <div className="form-group">
                              <label>Creato da:</label>
                              <span className="form-value">{getUserName(formData.redattoDa) || 'Non specificato'}</span>
                            </div>
                            {selectedRapportino && selectedRapportino.modificatoDa && (
                              <div className="form-group">
                                <label>Modificato da:</label>
                                <span className="form-value">{getUserName(selectedRapportino.modificatoDa) || 'Non specificato'}</span>
                              </div>
                            )}
                            {selectedRapportino && selectedRapportino.dataModifica && (
                              <div className="form-group">
                                <label>Data modifica:</label>
                                <span className="form-value">{formatRapportinoDate(selectedRapportino.dataModifica)}</span>
                              </div>
                            )}
                          </div>
                          <div className="form-actions">
                            <button 
                              onClick={() => handleQuickUpdate('data', formData.data)}
                              disabled={isUpdating || !formData.data}
                              className="btn btn-primary"
                            >
                              {isUpdating ? 'Aggiornamento...' : 'Aggiorna Data'}
                            </button>
                          </div>
                        </div>
                      );
              
                      case 'personale':
                        return (
                          <div className="quick-form-section">
                            <h3>👥 Selezione Personale</h3>
                            
                            {/* 🔧 FIX: Usa AutoPersonaleSection con selectedRapportino come editingRapportino */}
                            <AutoPersonaleSection 
                              users={users}
                              formData={formData}
                              setFormData={setFormData}
                              rapportinoDate={formData.data}
                              editingRapportino={selectedRapportino} // 🔧 FIX PRINCIPALE: Passa selectedRapportino
                              onRapportinoUpdate={loadRapportini}
                            />
                            
                            {/* Pulsante per aggiornare dopo le modifiche */}
                            <div className="form-actions">
                              <button 
                                onClick={() => handleQuickUpdate('personalePresente', formData.personalePresente)}
                                disabled={isUpdating}
                                className="btn btn-primary"
                              >
                                {isUpdating ? 'Aggiornamento...' : 'Aggiorna Personale'}
                              </button>
                            </div>
                          </div>
                        );
              
                    case 'mezzi':
                      return (
                        <div className="quick-form-section">
                          <h3>🚛 Mezzi Utilizzati</h3>
                          
                          {/* Ricerca con suggerimenti sempre in cima */}
                          <div className="search-section">
                            <SearchWithSuggestions
                              placeholder="🔍 Cerca mezzo per nome o targa..."
                              value={searchMezzi}
                              onChange={setSearchMezzi}
                              suggestions={getAvailableMezzi()}
                              onSelectSuggestion={handleSelectMezzo}
                              getSuggestionText={(mezzo) => `${mezzo.nomeMezzo || mezzo.nome || ''} ${mezzo.targa || ''}`.trim()}
                              renderSuggestion={(mezzo, searchTerm) => (
                                <div className="suggestion-content">
                                  <div className="suggestion-name">
                                    <strong>{mezzo.nomeMezzo || mezzo.nome}</strong>
                                  </div>
                                  <div className="suggestion-details">
                                    {mezzo.targa && <span className="suggestion-targa">🚗 {mezzo.targa}</span>}
                                    {mezzo.costoOrario && <span className="suggestion-cost">💰 €{mezzo.costoOrario}/h</span>}
                                  </div>
                                </div>
                              )}
                              maxSuggestions={8}
                            />
                          </div>
              
                          {/* Lista mezzi utilizzati */}
                          <div className="items-list">
                          {(formData.mezziUtilizzati || []).map((mezzo, index) => (
  <div key={index} className="list-item mezzo-item">
    {/* Info mezzo */}
    <div className="item-info">
      <strong>{mezzo.nomeMezzo || getMezzoName(mezzo.mezzoId)}</strong>
      {mezzo.targa && <span className="item-meta">🚗 {mezzo.targa}</span>}
    </div>
    
    {/* Bottone rimuovi */}
    <button 
      onClick={() => {
        const newMezzi = formData.mezziUtilizzati.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, mezziUtilizzati: newMezzi }));
        handleQuickUpdate('mezziUtilizzati', newMezzi);
      }}
      className="btn-remove"
      aria-label="Rimuovi mezzo"
    >
      🗑️
    </button>
    
    {/* Controlli inline */}
    <div className="item-controls">
      <div className="control-group-extended">
        <label>Ore</label>
        <input 
          type="number" 
          value={mezzo.ore || 0}
          onChange={(e) => {
            const newMezzi = [...formData.mezziUtilizzati];
            const oreValue = parseFloat(e.target.value) || 0;
            newMezzi[index] = {
              ...mezzo, 
              ore: oreValue,
              totale: oreValue * (mezzo.costoOrario || 0)
            };
            setFormData(prev => ({ ...prev, mezziUtilizzati: newMezzi }));
            clearTimeout(window.updateTimeout);
            window.updateTimeout = setTimeout(() => {
              handleQuickUpdate('mezziUtilizzati', newMezzi);
            }, 1000);
          }}
          min="0"
          step="0.5"
        />
        <span className="item-cost">
          {formatEuro(parseFloat(mezzo.totale || 0))}
        </span>
      </div>
    </div>
  </div>
))}
                            
                            {(formData.mezziUtilizzati || []).length === 0 && (
                              <div className="empty-list">
                                <p>Nessun mezzo aggiunto. Cerca e seleziona mezzi dal campo sopra.</p>
                              </div>
                            )}
                          </div>
                        </div>
                      );
              
                    case 'materiali-variabili':
                      return (
                        <div className="quick-form-section">
                          <h3>📦 Materiali a Costo Variabile</h3>
                          
                          {/* Ricerca con suggerimenti sempre in cima */}
                          <div className="search-section">
                            <SearchWithSuggestions
                              placeholder="🔍 Cerca materiale variabile..."
                              value={searchMatVariabili}
                              onChange={setSearchMatVariabili}
                              suggestions={getAvailableMaterialiVariabili()}
                              onSelectSuggestion={handleSelectMaterialeVariabile}
                              getSuggestionText={(materiale) => materiale.nomeMacrovoce || materiale.nome || ''}
                              renderSuggestion={(materiale, searchTerm) => (
                                <div className="suggestion-content">
                                  <div className="suggestion-name">
                                    <strong>{materiale.nomeMacrovoce || materiale.nome}</strong>
                                  </div>
                                  <div className="suggestion-details">
                                    <span className="suggestion-type">📦 Costo Variabile</span>
                                  </div>
                                </div>
                              )}
                              maxSuggestions={8}
                            />
                          </div>
              
                          {/* Lista materiali variabili */}
                          <div className="items-list">
                            {(formData.materialiVariabili || []).map((materiale, index) => (
                              <div key={index} className="list-item materiale-item">
                                <div className="item-info">
                                  <strong>{materiale.nomeMacrovoce || getMaterialeVariabileName(materiale.materialeId || materiale.id)}</strong>
                                </div>
                                <div className="item-controls vertical">
                                  <div className="control-group">
                                    <label>Descrizione:</label>
                                    <input 
                                      type="text" 
                                      value={materiale.descrizione || ''}
                                      onChange={(e) => {
                                        const newMateriali = [...formData.materialiVariabili];
                                        newMateriali[index] = {...materiale, descrizione: e.target.value};
                                        
                                        setFormData(prev => ({ ...prev, materialiVariabili: newMateriali }));
                                        
                                        clearTimeout(window.descTimeout);
                                        window.descTimeout = setTimeout(() => {
                                          handleQuickUpdate('materialiVariabili', newMateriali);
                                        }, 1500);
                                      }}
                                      placeholder="Descrizione facoltativa..."
                                      className="input-medium"
                                    />
                                  </div>
                                  <div className="control-group">
  <label>Costo €:</label>
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <input 
      type="number" 
      value={materiale.costo || 0}
      onChange={(e) => {
        const newMateriali = [...formData.materialiVariabili];
        newMateriali[index] = {...materiale, costo: parseFloat(e.target.value) || 0};
        
        setFormData(prev => ({ ...prev, materialiVariabili: newMateriali }));
        
        clearTimeout(window.costoTimeout);
        window.costoTimeout = setTimeout(() => {
          handleQuickUpdate('materialiVariabili', newMateriali);
        }, 1000);
      }}
      min="0"
      step="0.01"
      className="input-small"
    />
    <span className="formatted-amount">{formatEuro(materiale.costo || 0)}</span>
  </div>
</div>
                                </div>
                                <button 
                                  onClick={() => {
                                    const newMateriali = formData.materialiVariabili.filter((_, i) => i !== index);
                                    setFormData(prev => ({ ...prev, materialiVariabili: newMateriali }));
                                    handleQuickUpdate('materialiVariabili', newMateriali);
                                  }}
                                  className="btn-remove"
                                >
                                  🗑️
                                </button>
                              </div>
                            ))}
                            
                            {(formData.materialiVariabili || []).length === 0 && (
                              <div className="empty-list">
                                <p>Nessun materiale aggiunto. Cerca e seleziona materiali dal campo sopra.</p>
                              </div>
                            )}
                          </div>
                        </div>
                      );
              
                    case 'materiali-fissi':
                      return (
                        <div className="quick-form-section">
                          <h3>🔧 Materiali a Costo Fisso</h3>
                          
                          {/* Info costo trasporto */}
                          <div className="info-section">
                            <small>🚛 Costo trasporto cantiere: €{(cantiere.variazioneCostiMateriali || 0).toFixed(2)}/ton (aggiunto automaticamente)</small>
                          </div>
                          
                          {/* Ricerca con suggerimenti sempre in cima */}
                          <div className="search-section">
                            <SearchWithSuggestions
                              placeholder="🔍 Cerca materiale fisso..."
                              value={searchMatFissi}
                              onChange={setSearchMatFissi}
                              suggestions={getAvailableMaterialiFissi()}
                              onSelectSuggestion={handleSelectMaterialeFisso}
                              getSuggestionText={(materiale) => `${materiale.nomeMateriale || materiale.nome || ''} ${materiale.unitaMisura || ''}`.trim()}
                              renderSuggestion={(materiale, searchTerm) => (
                                <div className="suggestion-content">
                                  <div className="suggestion-name">
                                    <strong>{materiale.nomeMateriale || materiale.nome}</strong>
                                  </div>
                                  <div className="suggestion-details">
                                    <span className="suggestion-unit">📏 {materiale.unitaMisura || 'pz'}</span>
                                    {materiale.prezzoUnitario && <span className="suggestion-price">💰 €{materiale.prezzoUnitario}</span>}
                                  </div>
                                </div>
                              )}
                              maxSuggestions={8}
                            />
                          </div>
              
                          {/* Lista materiali fissi */}
                          <div className="items-list">
                            {(formData.materialiFissi || []).map((materiale, index) => (
                              <div key={index} className="list-item materiale-item">
                                <div className="item-info">
                                  <strong>{materiale.nomeMateriale || getMaterialeFissoName(materiale.materialeId || materiale.id)}</strong>
                                  <span className="item-meta">{materiale.unitaMisura || 'pz'}</span>
                                </div>
                                <div className="item-controls">
  <label>Quantità:</label>
  <input 
    type="number" 
    value={materiale.quantita || 0}
    onChange={(e) => {
      const newMateriali = [...formData.materialiFissi];
      const quantitaValue = parseFloat(e.target.value) || 0;
      const updatedMateriale = {
        ...materiale, 
        quantita: quantitaValue,
        totale: quantitaValue * (materiale.prezzoUnitario || 0)
      };
      newMateriali[index] = updatedMateriale;
      
      setFormData(prev => ({ ...prev, materialiFissi: newMateriali }));
      
      clearTimeout(window.quantitaTimeout);
      window.quantitaTimeout = setTimeout(() => {
        handleQuickUpdate('materialiFissi', newMateriali);
      }, 1000);
    }}
    min="0"
    step="0.01"
    className="input-small"
  />
  {materiale.totale !== undefined && (
    <div className="item-cost">{formatEuro(parseFloat(materiale.totale || 0))}</div>
  )}
</div>
    <button 
      onClick={() => {
        const newMateriali = formData.materialiFissi.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, materialiFissi: newMateriali }));
        handleQuickUpdate('materialiFissi', newMateriali);
      }}
      className="btn-remove"
    >
      🗑️
    </button>
  </div>
))}

{(formData.materialiFissi || []).length === 0 && (
  <div className="empty-list">
    <p>Nessun materiale aggiunto. Cerca e seleziona materiali dal campo sopra.</p>
  </div>
)}
</div>
</div>
);

case 'lavorazioni':
return (
<div className="quick-form-section">
<h3>⚡ Lavorazioni Eseguite</h3>

{/* Ricerca con suggerimenti sempre in cima */}
<div className="search-section">
<SearchWithSuggestions
  placeholder="🔍 Cerca lavorazione..."
  value={searchLavorazioni}
  onChange={setSearchLavorazioni}
  suggestions={getAvailableLavorazioni()}
  onSelectSuggestion={handleSelectLavorazione}
  getSuggestionText={(lavorazione) => lavorazione.nomeLavorazione || lavorazione.nome || ''}
  renderSuggestion={(lavorazione, searchTerm) => (
    <div className="suggestion-content">
      <div className="suggestion-name">
        <strong>{lavorazione.nomeLavorazione || lavorazione.nome}</strong>
      </div>
      <div className="suggestion-details">
        {lavorazione.descrizione && <span className="suggestion-desc">{lavorazione.descrizione}</span>}
      </div>
    </div>
  )}
  maxSuggestions={8}
/>

{/* Campo per lavorazione personalizzata */}
<div className="custom-add-section">
  <input 
    type="text"
    placeholder="➕ Oppure scrivi una nuova lavorazione personalizzata..."
    className="custom-input"
    onKeyPress={(e) => {
      if (e.key === 'Enter' && e.target.value.trim()) {
        const lavorazioniPresenti = formData.lavorazioni || [];
        const newLavorazione = {
          lavorazioneId: `custom_${Date.now()}`,
          nomeLavorazione: e.target.value.trim(),
          descrizione: '',
          note: '',
          isCustom: true
        };
        const newLavorazioni = [...lavorazioniPresenti, newLavorazione];
        
        setFormData(prev => ({ ...prev, lavorazioni: newLavorazioni }));
        handleQuickUpdate('lavorazioni', newLavorazioni);
        e.target.value = '';
      }
    }}
  />
</div>
</div>

{/* Lista lavorazioni */}
<div className="items-list">
{(formData.lavorazioni || []).map((lavorazione, index) => (
  <div key={index} className="list-item lavorazione-item">
    <div className="item-info">
      <strong>{lavorazione.nomeLavorazione || getLavorazioneName(lavorazione.lavorazioneId || lavorazione.id)}</strong>
      {lavorazione.isCustom && <span className="custom-badge">PERSONALIZZATA</span>}
      {lavorazione.note && <span className="item-note">{lavorazione.note}</span>}
    </div>
    <button 
      onClick={() => {
        const newLavorazioni = formData.lavorazioni.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, lavorazioni: newLavorazioni }));
        handleQuickUpdate('lavorazioni', newLavorazioni);
      }}
      className="btn-remove"
    >
      🗑️
    </button>
  </div>
))}

{(formData.lavorazioni || []).length === 0 && (
  <div className="empty-list">
    <p>Nessuna lavorazione aggiunta. Cerca lavorazioni o aggiungi personalizzate dai campi sopra.</p>
  </div>
)}
</div>
</div>
);

case 'ricavi':
return (
<div className="quick-form-section">
<h3>💰 Ricavi</h3>

{/* Pulsante per aggiungere nuovo ricavo */}
<div className="add-section">
<button 
  onClick={() => {
    const newRicavo = { descrizione: '', importo: 0 };
    const newRicavi = [...(formData.ricavi || []), newRicavo];
    setFormData(prev => ({ ...prev, ricavi: newRicavi }));
    handleQuickUpdate('ricavi', newRicavi);
  }}
  className="btn btn-secondary"
>
  ➕ Aggiungi Nuovo Ricavo
</button>
</div>

{/* Lista ricavi */}
<div className="items-list">
{(formData.ricavi || []).map((ricavo, index) => (
  <div key={index} className="list-item ricavo-item">
    <div className="item-controls vertical">
      <div className="control-group">
        <label>Descrizione:</label>
        <input 
          type="text" 
          value={ricavo.descrizione || ''}
          onChange={(e) => {
            const newRicavi = [...formData.ricavi];
            newRicavi[index] = {...ricavo, descrizione: e.target.value};
            setFormData(prev => ({ ...prev, ricavi: newRicavi }));
          }}
          placeholder="Descrizione del ricavo"
          className="input-medium"
        />
      </div>
      <div className="control-group">
  <label>Importo €:</label>
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <input 
      type="number" 
      value={ricavo.importo || 0}
      onChange={(e) => {
        const newRicavi = [...formData.ricavi];
        newRicavi[index] = {...ricavo, importo: parseFloat(e.target.value) || 0};
        setFormData(prev => ({ ...prev, ricavi: newRicavi }));
        handleQuickUpdate('ricavi', newRicavi);
      }}
      min="0"
      step="0.01"
      className="input-small"
    />
    <span className="formatted-amount">{formatEuro(ricavo.importo || 0)}</span>
  </div>
</div>
    </div>
    <button 
      onClick={() => {
        const newRicavi = formData.ricavi.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, ricavi: newRicavi }));
        handleQuickUpdate('ricavi', newRicavi);
      }}
      className="btn-remove"
    >
      🗑️
    </button>
  </div>
))}

{(formData.ricavi || []).length === 0 && (
  <div className="empty-list">
    <p>Nessun ricavo aggiunto. Clicca "Aggiungi Nuovo Ricavo" per iniziare.</p>
  </div>
)}
</div>
</div>
);

case 'spese-varie':
return (
<div className="quick-form-section">
<h3>💳 Spese Varie</h3>

{/* Info box */}
<div className="info-box" style={{ 
  background: '#f8f9fa', 
  padding: '10px', 
  borderRadius: '6px', 
  marginBottom: '15px',
  fontSize: '0.85em'
}}>
  💡 Le spese inserite qui verranno aggiunte alla categoria selezionata nel riepilogo economico.
</div>

{/* Pulsante per aggiungere nuova spesa */}
<div className="add-section">
<button 
  onClick={() => {
    const newSpesa = { descrizione: '', importo: 0, categoria: 'Spese Varie' };
    const newSpese = [...(formData.speseVarie || []), newSpesa];
    setFormData(prev => ({ ...prev, speseVarie: newSpese }));
    handleQuickUpdate('speseVarie', newSpese);
  }}
  className="btn btn-secondary"
>
  ➕ Aggiungi Nuova Spesa
</button>
</div>

{/* Lista spese varie */}
<div className="items-list">
{(formData.speseVarie || []).map((spesa, index) => (
  <div key={index} className="list-item spesa-item">
    <div className="item-controls vertical">
      <div className="control-group">
        <label>Descrizione:</label>
        <input 
          type="text" 
          value={spesa.descrizione || ''}
          onChange={(e) => {
            const newSpese = [...formData.speseVarie];
            newSpese[index] = {...spesa, descrizione: e.target.value};
            setFormData(prev => ({ ...prev, speseVarie: newSpese }));
          }}
          placeholder="Es: Costo Personale fino al 31/12/2025"
          className="input-medium"
        />
      </div>
      <div className="control-group">
        <label>Importo €:</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input 
            type="number" 
            value={spesa.importo || 0}
            onChange={(e) => {
              const newSpese = [...formData.speseVarie];
              newSpese[index] = {...spesa, importo: parseFloat(e.target.value) || 0};
              setFormData(prev => ({ ...prev, speseVarie: newSpese }));
              handleQuickUpdate('speseVarie', newSpese);
            }}
            min="0"
            step="0.01"
            className="input-small"
          />
          <span className="formatted-amount">{formatEuro(spesa.importo || 0)}</span>
        </div>
      </div>
      <div className="control-group">
        <label>Categoria Report:</label>
        <select
          value={spesa.categoria || 'Spese Varie'}
          onChange={(e) => {
            const newSpese = [...formData.speseVarie];
            newSpese[index] = {...spesa, categoria: e.target.value};
            setFormData(prev => ({ ...prev, speseVarie: newSpese }));
            handleQuickUpdate('speseVarie', newSpese);
          }}
          className="input-small"
          style={{ 
            borderLeft: `3px solid ${
              spesa.categoria === 'Personale' ? '#3498db' :
              spesa.categoria === 'Mezzi' ? '#e74c3c' :
              spesa.categoria === 'Materiali Variabili' ? '#2ecc71' :
              spesa.categoria === 'Materiali Fissi' ? '#27ae60' :
              '#9b59b6'
            }`,
            fontWeight: '500'
          }}
        >
          <option value="Personale">👷 Personale</option>
          <option value="Mezzi">🚛 Mezzi</option>
          <option value="Materiali Variabili">📦 Mat. Variabili</option>
          <option value="Materiali Fissi">🏗️ Mat. Fissi</option>
          <option value="Spese Varie">💳 Spese Varie</option>
        </select>
      </div>
    </div>
    <button 
      onClick={() => {
        const newSpese = formData.speseVarie.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, speseVarie: newSpese }));
        handleQuickUpdate('speseVarie', newSpese);
      }}
      className="btn-remove"
    >
      🗑️
    </button>
  </div>
))}

{(formData.speseVarie || []).length === 0 && (
  <div className="empty-list">
    <p>Nessuna spesa inserita. Clicca "Aggiungi Nuova Spesa" per iniziare.</p>
  </div>
)}
</div>
</div>
);

default:
return <div>Sezione non trovata</div>;
}
};

if (isLoading) {
return (
<div className="loading-container">
<div className="loading-spinner"></div>
<p>Caricamento rapportini...</p>
</div>
);
}

return (
<div className="rapportini-split-view">
{/* Notifiche */}
{notification.show && (
<div className={`notification ${notification.type}`}>
{notification.message}
</div>
)}

{/* Header */}
<div className="split-view-header">
<div className="header-left">
<button className="btn-back" onClick={onBack}>
← Torna al Cantiere
</button>
<div className="header-title">
<h2>📝 Rapportini - {cantiere.nomeCommessa}</h2>
<p>{rapportini.length} rapportini trovati</p>
</div>
</div>
<div className="header-actions">
  
  <button 
    className="btn btn-primary"
    onClick={handleNewRapportino}
  >
    ➕ Nuovo Rapportino
  </button>
</div>
</div>

{/* Split View Container */}
<div className="split-view-container">
  {/* Lista Rapportini - Sinistra */}
  <div className="split-view-left">
    <div className="rapportini-list">
      <h3>Lista Rapportini</h3>
      
      {/* Campo di ricerca */}
      <div className="search-rapportini">
        <div className="search-input-container">
          <input
            type="text"
            placeholder="🔍 Cerca rapportini per data, redattore, note..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-rapportini-input"
          />
          {searchTerm && (
            <button 
              onClick={() => setSearchTerm('')}
              className="clear-search-btn"
              title="Cancella ricerca"
            >
              ✕
            </button>
          )}
        </div>
        {searchTerm && (
          <div className="search-results-info">
            {filteredRapportini.length} di {rapportini.length} rapportini
          </div>
        )}
      </div>
      
      {/* Lista rapportini filtrata */}
      {filteredRapportini.length === 0 ? (
        <div className="empty-state">
          {searchTerm ? (
            <div>
              <p>Nessun rapportino trovato per "{searchTerm}"</p>
              <button 
                onClick={() => setSearchTerm('')}
                className="btn btn-secondary btn-sm"
              >
                Cancella ricerca
              </button>
            </div>
          ) : (
            <p>Nessun rapportino trovato</p>
          )}
        </div>
      ) : (
        <div className="rapportini-items">
          {filteredRapportini.map((rapportino) => {
            if (!rapportino || !rapportino.id) {
              console.warn('Rapportino non valido:', rapportino);
              return null;
            }
            
            return (
              <div 
                key={rapportino.id}
                className={`rapportino-item ${selectedRapportino?.id === rapportino.id ? 'selected' : ''} ${openSettingsDropdown === rapportino.id ? 'settings-open' : ''}`}
                onClick={() => handleSelectRapportino(rapportino)}
              >
                <div className="rapportino-name">
                  {/* 🆕 WRAPPER CON FLEX PER DATA + BADGE */}
                  <div className="rapportino-header-wrapper">
                    <strong>{formatRapportinoDate(rapportino.data)}</strong>
                    {/* 🆕 BADGE SINCRONIZZAZIONE */}
                    <SyncStatusBadge rapportino={rapportino} />
                  </div>
                  
                  <span className="rapportino-date">
                    Creato: {formatRapportinoDate(rapportino.createdAt)}
                  </span>
                  <span className="rapportino-redattore">
                    Da: {getUserName(rapportino.redattoDa)}
                  </span>
                </div>
                <div className="rapportino-actions">
                  <button 
                    className="settings-btn"
                    onClick={(e) => handleOpenSettings(rapportino.id, e)}
                    title="Opzioni rapportino"
                  >
                    ⚙️
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  </div>

{/* Form Modifica Rapida - Destra */}
<div className="split-view-right">
<div className="quick-edit-form">
{/* Menu Selezione Settore */}
<div className="section-selector">
<h3>Modifica Rapida</h3>
<div className="section-tabs">
  {sections.map((section) => (
    <button
      key={section.id}
      className={`section-tab ${selectedSection === section.id ? 'active' : ''}`}
      onClick={() => setSelectedSection(section.id)}
    >
      <span className="section-icon">{section.icon}</span>
      <span className="section-label">{section.label}</span>
    </button>
  ))}
</div>
</div>

 {/* 🆕 AGGIUNGI QUI L'INDICATORE */}
 {selectedRapportino && (
      <DistributionStatusIndicator rapportino={selectedRapportino} />
    )}

{/* Contenuto Sezione Selezionata */}
<div className="section-content">
{renderQuickFormSection()}
</div>
</div>
</div>
</div>

 {/* Modal Form Avanzato CON SEZIONI COLLASSABILI */}
      {showForm && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && resetForm()}>
          <div className="modal-content large-modal">
            <div className="modal-header">
              <h3>
                {editingRapportino ? 
                  `✏️ Modifica Rapportino del ${formatRapportinoDate(editingRapportino.data)}` : 
                  '➕ Nuovo Rapportino'
                }
              </h3>
              <button className="modal-close" onClick={resetForm}>✕</button>
            </div>

            <form onSubmit={handleFormSubmit} className="rapportino-form">
            <div className="modal-body" ref={scrollContainerRef}>
                {/* Controllo di sicurezza: Verifica formData prima del rendering */}
                {!formData || typeof formData !== 'object' ? (
                  <div className="alert alert-danger">
                    <h4>Errore nel caricamento del form</h4>
                    <p>Si è verificato un errore nel caricamento dei dati. Ricarica la pagina e riprova.</p>
                    <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
                      Ricarica Pagina
                    </button>
                  </div>
                ) : (
                  <>
                    {/* NUOVO: Controlli sezioni su mobile */}
                    <SectionControls
  onExpandAll={handleExpandAllSections}  // ✅ NUOVO
  onCollapseAll={handleCollapseAllSections}  // ✅ NUOVO
  totalSections={formSections.length}
  collapsedCount={collapsedSections.size}
/>

                    {/* MODIFICA: Informazioni Base - ora collassabile */}
                    <CollapsibleFormSection
                      id="informazioni-base"
                      title="Informazioni Base"
                      icon="📅"
                      priority="high"
                      isCollapsed={isSectionCollapsed('informazioni-base')}
                      onToggle={toggleSection}
                    >
                      <div className="form-row">
                        <div className="form-group">
                          <label>Data Rapportino *</label>
                          <input
                            type="date"
                            value={formData.data || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, data: e.target.value }))}
                            max={new Date().toISOString().split('T')[0]}
                            required
                            className="form-control"
                          />
                        </div>
                        <div className="form-group">
                          <label>Redatto da</label>
                          <input
                            type="text"
                            value={(() => {
                              if (currentUser?.nome && currentUser?.cognome) {
                                return `${currentUser.nome} ${currentUser.cognome}`;
                              }
                              return currentUser?.email || 'Utente non identificato';
                            })()}
                            disabled
                            className="form-control"
                          />
                        </div>
                        {/* Aggiungi informazioni di modifica solo se esistono */}
                        {editingRapportino && (
                          <>
                            <div className="form-group">
                              <label>Ultima modifica</label>
                              <input
                                type="text"
                                value={(() => {
                                  if (editingRapportino.dataModifica) {
                                    return formatRapportinoDate(editingRapportino.dataModifica);
                                  }
                                  return 'Mai modificato';
                                })()}
                                disabled
                                className="form-control"
                              />
                            </div>
                            <div className="form-group">
                              <label>Modificato da</label>
                              <input
                                type="text"
                                value={getUserName(editingRapportino.modificatoDa) || 'Non specificato'}
                                disabled
                                className="form-control"
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </CollapsibleFormSection>

                    {/* Sezioni del Rapportino CON COLLAPSIBLE */}
                    <CollapsibleFormSection
  title="👷 Personale Presente"
  sectionKey="personale-presente"
  isCollapsed={isSectionCollapsed('personale-presente')}
  onToggle={() => toggleSection('personale-presente')}
>
  <PersonaleSectionWrapper />
</CollapsibleFormSection>
<CollapsibleFormSection
  title="🚛 Mezzi Utilizzati"
  sectionKey="mezzi-utilizzati"
  isCollapsed={isSectionCollapsed('mezzi-utilizzati')}
  onToggle={() => toggleSection('mezzi-utilizzati')}
>
  <MezziSection />
</CollapsibleFormSection>
<CollapsibleFormSection
  title="📦 Materiali a Costo Variabile"
  sectionKey="materiali-variabili"
  isCollapsed={isSectionCollapsed('materiali-variabili')}
  onToggle={() => toggleSection('materiali-variabili')}
>
  <MaterialiVariabiliSection />
</CollapsibleFormSection>
<CollapsibleFormSection
  title="🏗️ Materiali a Costo Fisso"
  sectionKey="materiali-fissi"
  isCollapsed={isSectionCollapsed('materiali-fissi')}
  onToggle={() => toggleSection('materiali-fissi')}
>
  <MaterialiFissiSection />
</CollapsibleFormSection>
<CollapsibleFormSection
  title="⚒️ Lavorazioni"
  sectionKey="lavorazioni"
  isCollapsed={isSectionCollapsed('lavorazioni')}
  onToggle={() => toggleSection('lavorazioni')}
>
  <LavorazioniSection />
</CollapsibleFormSection>
<CollapsibleFormSection
  title="💰 Ricavi"
  sectionKey="ricavi"
  isCollapsed={isSectionCollapsed('ricavi')}
  onToggle={() => toggleSection('ricavi')}
>
  <RicaviSection />
</CollapsibleFormSection>
<CollapsibleFormSection
  title="💳 Spese Varie"
  sectionKey="spese-varie"
  isCollapsed={isSectionCollapsed('spese-varie')}
  onToggle={() => toggleSection('spese-varie')}
>
  <SpeseVarieSection />
</CollapsibleFormSection>
<CollapsibleFormSection
  title="📎 Allegati"
  sectionKey="allegati"
  isCollapsed={isSectionCollapsed('allegati')}
  onToggle={() => toggleSection('allegati')}
>
  <AllegatiSection />
</CollapsibleFormSection>

                    {/* MODIFICA: Note Varie - ora collassabile */}
                    <CollapsibleFormSection
                      id="note-aggiuntive"
                      title="Note Varie"
                      icon="📝"
                      priority="low"
                      isCollapsed={isSectionCollapsed('note-aggiuntive')}
                      onToggle={toggleSection}
                    >
                      <div className="form-group">
                        <label>Note aggiuntive</label>
                        <textarea
                          value={formData.noteVarie || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, noteVarie: e.target.value }))}
                          placeholder="Inserisci eventuali note aggiuntive..."
                          rows="4"
                          className="form-control"
                        />
                      </div>
                    </CollapsibleFormSection>

{/* MODIFICA: Riepilogo Totali - ora collassabile */}
<CollapsibleFormSection
  id="totali"
  title="Riepilogo Economico"
  icon="💰"
  priority="high"
  isCollapsed={isSectionCollapsed('totali')}
  onToggle={toggleSection}
>
<div className="totali-grid">
  {(() => {
    const totali = getTotaliRapportino();
    return (
      <>
        <div className="totale-item">
          <span>👷 Costi Personale:</span>
          <span>{formatEuro(totali.totalePersonale)}</span>
        </div>
        <div className="totale-item">
          <span>🚛 Costi Mezzi:</span>
          <span>{formatEuro(totali.totaleMezzi)}</span>
        </div>
        <div className="totale-item">
          <span>📦 Materiali Variabili:</span>
          <span>{formatEuro(totali.totaleMatVariabili)}</span>
        </div>
        <div className="totale-item">
          <span>🏗️ Materiali Fissi:</span>
          <span>{formatEuro(totali.totaleMatFissi)}</span>
        </div>
        {/* 🆕 AGGIUNGI QUESTA RIGA */}
        {totali.totaleSpeseVarie > 0 && (
          <div className="totale-item">
            <span>💳 Spese Varie:</span>
            <span>{formatEuro(totali.totaleSpeseVarie)}</span>
          </div>
        )}
        <div className="totale-item totale-costi">
          <span><strong>Totale Costi:</strong></span>
          <span><strong>{formatEuro(totali.totaleCosti)}</strong></span>
        </div>
          <div className="totale-item totale-ricavi">
            <span><strong>Totale Ricavi:</strong></span>
            <span><strong>{formatEuro(totali.totaleRicavi)}</strong></span>
          </div>
          <div className={`totale-item totale-margine ${totali.margine >= 0 ? 'positive' : 'negative'}`}>
            <span><strong>Margine:</strong></span>
            <span><strong>{formatEuro(totali.margine)}</strong></span>
          </div>
        </>
      );
    })()}
  </div>
</CollapsibleFormSection>
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Annulla
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={!formData || typeof formData !== 'object'}
                >
                  {editingRapportino ? 'Aggiorna Rapportino' : 'Crea Rapportino'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
{/* Settings Dropdown Portal */}
{openSettingsDropdown && (
  <SettingsDropdownPortal
    top={dropdownPosition.top}
    left={dropdownPosition.left}
    isMobile={isMobile}
    onClose={() => setOpenSettingsDropdown(null)}
  >
    <button 
      onClick={handleModificaAvanzata}
      className="success"
    >
      <span>✏️</span>
      Modifica Avanzata
    </button>

    <button 
      onClick={() => {
        handleSyncRapportino(selectedRapportino);
        setOpenSettingsDropdown(null);
      }}
      className="info"
    >
      <span>🔄</span>
      Sincronizza Ore
    </button>

    <button 
      onClick={handleDuplicaRapportino}
      className="warning"
    >
      <span>📋</span>
      Duplica Rapportino
    </button>
    
    <button 
      onClick={handleEliminaRapportino}
      className="danger"
    >
      <span>🗑️</span>
      Elimina Rapportino
    </button>
  </SettingsDropdownPortal>
)}

{/* 🆕 MODAL COPIA MEZZI DA GIORNO PRECEDENTE */}
{showCopyMezziModal && (
  <div className="modal-overlay" onClick={() => setShowCopyMezziModal(false)}>
    <div className="modal-content copy-mezzi-modal" onClick={(e) => e.stopPropagation()}>
      <div className="modal-header">
        <h3>📋 Copia Mezzi da Rapportino Precedente</h3>
        <button 
          className="modal-close" 
          onClick={() => setShowCopyMezziModal(false)}
          aria-label="Chiudi"
        >
          ×
        </button>
      </div>
      
      <div className="modal-body">
        <p className="modal-description">
          Seleziona il rapportino da cui vuoi copiare i mezzi e le ore di utilizzo:
        </p>
        
        {availableDatesForCopy.length === 0 ? (
          <div className="alert alert-info">
            Non ci sono rapportini precedenti con mezzi da copiare.
          </div>
        ) : (
          <div className="dates-list">
            {availableDatesForCopy.map((rapportino) => (
              <label 
                key={rapportino.id}
                className={`date-option ${selectedDateForCopy === rapportino.data ? 'selected' : ''}`}
              >
                <input
                  type="radio"
                  name="copy-date"
                  value={rapportino.data}
                  checked={selectedDateForCopy === rapportino.data}
                  onChange={(e) => setSelectedDateForCopy(e.target.value)}
                />
                <div className="date-info">
                  <div className="date-display">{rapportino.displayDate}</div>
                  <div className="date-mezzi-count">
                    {rapportino.numeroMezzi} {rapportino.numeroMezzi === 1 ? 'mezzo' : 'mezzi'} utilizzati
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}
      </div>
      
      <div className="modal-footer">
        <button 
          type="button" 
          className="btn btn-secondary" 
          onClick={() => setShowCopyMezziModal(false)}
        >
          Annulla
        </button>
        <button 
          type="button" 
          className="btn btn-primary"
          onClick={handleCopyMezziFromDate}
          disabled={!selectedDateForCopy}
        >
          Copia Mezzi
        </button>
      </div>
    </div>
  </div>
)}

{/* 🆕 MODAL NUOVO MATERIALE VARIABILE */}
{showNewMaterialeVariabileModal && (
  <div className="modal-overlay" onClick={() => setShowNewMaterialeVariabileModal(false)}>
    <div className="modal-content new-material-modal" onClick={(e) => e.stopPropagation()}>
      <div className="modal-header">
        <h3>➕ Nuovo Materiale a Costo Variabile</h3>
        <button 
          className="modal-close" 
          onClick={() => setShowNewMaterialeVariabileModal(false)}
          aria-label="Chiudi"
        >
          ×
        </button>
      </div>
      
      <div className="modal-body">
        <div className="form-group">
          <label>Nome Materiale / Macrovoce *</label>
          <input
            type="text"
            value={newMaterialeVariabileData.nomeMacrovoce}
            onChange={(e) => setNewMaterialeVariabileData(prev => ({
              ...prev,
              nomeMacrovoce: e.target.value
            }))}
            placeholder="Es: Trasporto materiale, Noleggio gru..."
            className="form-control"
            autoFocus
          />
          <small className="form-text">
            💡 Il nome dovrebbe descrivere la categoria o il tipo di costo variabile
          </small>
        </div>
      </div>
      
      <div className="modal-footer">
        <button 
          type="button" 
          className="btn btn-secondary" 
          onClick={() => setShowNewMaterialeVariabileModal(false)}
          disabled={isCreatingMateriale}
        >
          Annulla
        </button>
        <button 
          type="button" 
          className="btn btn-success"
          onClick={handleCreateMaterialeVariabile}
          disabled={!newMaterialeVariabileData.nomeMacrovoce.trim() || isCreatingMateriale}
        >
          {isCreatingMateriale ? '⏳ Creazione...' : '✅ Crea Materiale'}
        </button>
      </div>
    </div>
  </div>
)}

{/* 🆕 MODAL NUOVO MATERIALE FISSO */}
{showNewMaterialeFissoModal && (
  <div className="modal-overlay" onClick={() => setShowNewMaterialeFissoModal(false)}>
    <div className="modal-content new-material-modal" onClick={(e) => e.stopPropagation()}>
      <div className="modal-header">
        <h3>➕ Nuovo Materiale a Costo Fisso</h3>
        <button 
          className="modal-close" 
          onClick={() => setShowNewMaterialeFissoModal(false)}
          aria-label="Chiudi"
        >
          ×
        </button>
      </div>
      
      <div className="modal-body">
        <div className="form-group">
          <label>Nome Materiale *</label>
          <input
            type="text"
            value={newMaterialeFissoData.nomeMateriale}
            onChange={(e) => setNewMaterialeFissoData(prev => ({
              ...prev,
              nomeMateriale: e.target.value
            }))}
            placeholder="Es: Cemento, Ferro, Sabbia..."
            className="form-control"
            autoFocus
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Prezzo Unitario (€) *</label>
            <input
              type="number"
              value={newMaterialeFissoData.prezzoUnitario}
              onChange={(e) => setNewMaterialeFissoData(prev => ({
                ...prev,
                prezzoUnitario: e.target.value
              }))}
              step="0.01"
              min="0.01"
              placeholder="25.50"
              className="form-control"
            />
          </div>

          <div className="form-group">
            <label>Unità di Misura *</label>
            <select
              value={newMaterialeFissoData.unitaMisura}
              onChange={(e) => setNewMaterialeFissoData(prev => ({
                ...prev,
                unitaMisura: e.target.value
              }))}
              className="form-control"
            >
              <option value="pz">pz (pezzo)</option>
              <option value="ton">ton (tonnellata)</option>
              <option value="m³">m³ (metro cubo)</option>
              <option value="m²">m² (metro quadro)</option>
              <option value="ml">ml (metro lineare)</option>
              <option value="kg">kg (chilogrammo)</option>
              <option value="lt">lt (litro)</option>
              <option value="sacco">sacco</option>
              <option value="bancale">bancale</option>
            </select>
          </div>
        </div>

        <small className="form-text">
          💡 Il prezzo unitario è il costo base per unità. Il costo di trasporto del cantiere verrà aggiunto automaticamente.
        </small>
      </div>
      
      <div className="modal-footer">
        <button 
          type="button" 
          className="btn btn-secondary" 
          onClick={() => setShowNewMaterialeFissoModal(false)}
          disabled={isCreatingMateriale}
        >
          Annulla
        </button>
        <button 
          type="button" 
          className="btn btn-success"
          onClick={handleCreateMaterialeFisso}
          disabled={
            !newMaterialeFissoData.nomeMateriale.trim() || 
            !newMaterialeFissoData.prezzoUnitario ||
            !newMaterialeFissoData.unitaMisura ||
            isCreatingMateriale
          }
        >
          {isCreatingMateriale ? '⏳ Creazione...' : '✅ Crea Materiale'}
        </button>
      </div>
    </div>
  </div>
)}

  {/* 🆕 MODAL SELEZIONE DATA */}
  <DatePickerModal
      isOpen={showDateModal}
      onClose={handleDateCancel}
      onConfirm={handleDateConfirm}
      originalDate={rapportinoToDuplicate?.data}
      title="Seleziona Data Rapportino Duplicato"
    />
    </div>
  );
};

export default RapportiniManagement;